//src/admin/pages/AdminReports.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getAdminAnalyticsCards } from "../../services/adminAnalyticsCards.api";
import { FaCalendarAlt, FaExchangeAlt, FaSearch } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import {
  DocumentTextIcon,
  ChartBarIcon as ChartSolid,
} from "@heroicons/react/24/solid";
import { Line, Bar } from "react-chartjs-2";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const formatLocalDate = (date) => {
  if (!date) return undefined;

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
};

const parseChartDate = (value) => {
  if (!value) return new Date();

  if (value instanceof Date) {
    return value;
  }

  const text = String(value);

  // Handles ISO strings like 2026-05-10T13:25:00.000Z
  if (text.includes("T")) {
    return new Date(text);
  }

  // Handles SQL datetime strings like 2026-05-10 13:25:00
  if (text.includes(" ")) {
    return new Date(text.replace(" ", "T"));
  }

  // Handles date-only strings like 2026-05-10
  return new Date(`${text}T00:00:00`);
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const diffDaysInclusive = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);

  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);

  return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
};

function AdminReports() {
  const { lang } = useContext(AdminLanguageContext);
  const location = useLocation();

  const navigate = useNavigate();
const cameFromAnalytics = Boolean(location.state?.fromAnalytics);

  const analyticsStateAppliedRef = React.useRef(false);

  const [isOpen, setIsOpen] = useState(false);

  const [cardsData, setCardsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [loadError, setLoadError] = useState("");

  const modalRef = React.useRef(null);

const [showCompareCalendar, setShowCompareCalendar] = useState(false);

const [dateView, setDateView] = useState("main");
const [dateTab, setDateTab] = useState("today");

const [activeQuickRange, setActiveQuickRange] = useState(null);

const [rangeStart, setRangeStart] = useState(null);
const [rangeEnd, setRangeEnd] = useState(null);

const [mainRange, setMainRange] = useState(null);

const [selectedDate, setSelectedDate] = useState(new Date());
const [calendarBaseDate, setCalendarBaseDate] = useState(new Date());
const [calendarOffset, setCalendarOffset] = useState(0);

const [reportSearch, setReportSearch] = useState("");

  const [mainStart, setMainStart] = useState(null);
const [mainEnd, setMainEnd] = useState(null);

  const [compareRange, setCompareRange] = useState(null);
const [showCompareDropdown, setShowCompareDropdown] = useState(false);
const [compareType, setCompareType] = useState("none");

const [showCustomCompareModal, setShowCustomCompareModal] = useState(false);
const [compareStartDate, setCompareStartDate] = useState(null);
const [compareEndDate, setCompareEndDate] = useState(null);

  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(89000);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [tempRate, setTempRate] = useState("");

  const [selectedReport, setSelectedReport] = useState(null);

  const [showExportModal, setShowExportModal] = useState(false);

  const [exportMode, setExportMode] = useState(null); // "all" | "tables"

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
  const isAdmin = adminUser.role === "admin";
  const canAccess = isAdmin || perms.can_view_analytics;

  const TEXT = {
    EN: {
      title: "Reports",
      reports: "Reports",
      chooseExportFormat: "Choose export format",
exportAllPageDescription: "Export the full report page including chart and tables.",
exportTablesDescription: "Export only the main and comparison tables.",
print: "Print",
pdf: "PDF",
csv: "CSV",
excel: "Excel",
backToExportOptions: "Back",
mainTable: "Main table",
comparisonTable: "Comparison table",
      export: "Export",
      analytics: "Analytics",
exportReport: "Export report",
exportAllPage: "Export all page",
exportWithoutChart: "Export without chart",
exportLaterNote: "Export functionality will be implemented later.",
      name: "Name",
      category: "Category",
      createdBy: "Created by",
      admin: "Admin",
      today: "Today",
      noComparison: "No comparison",
      previousPeriod: "Previous period",
      previousYear: "Previous year",
      currencyRateTitle: "Set exchange rate",
      usdToLbp: "1 USD = ? LBP",
      save: "Save",
      cancel: "Cancel",
      back: "Back to reports",
      details: "Details",
      compareNone: "No comparison",
compareYesterday: "Yesterday | Previous period",
comparePrevYear: "Previous year",
comparePrevYearMatch: "Previous year (match day of week)",
compareCustom: "Custom",
cancelBtn: "Cancel",
selectedRange: "Selected range",
      noData: "No data for this date range",
      mainDate: "Main date",
      compareDate: "Compare date",
      value: "Value",
      total: "Total",
      comparison: "Comparison",
      totalSalesOverTime: "Total sales over time",
      grossSales: "Gross sales",
      returningRate: "Returning customer rate",
      ordersFulfilled: "Orders fulfilled",
      totalOrders: "Total orders",
      totalSalesBreakdown: "Total sales breakdown",
      salesByChannel: "Total sales by sales channel",
      avgOrder: "Average order value over time",
      salesByProduct: "Total sales by product",
      sessionsOverTime: "Sessions over time",
      conversion: "Conversion rate over time",
      conversionBreakdown: "Conversion rate breakdown",
      topProducts: "Top selling products",
      sessionsByDevice: "Sessions by device type",
      sessionsByLocation: "Sessions by location",
      salesBySocial: "Total sales by social referrer",
      cohortAnalysis: "Customer cohort analysis",
      landingPage: "Sessions by landing page",
      sessionsBySocialReferrer: "Sessions by social referrer",
      salesByReferrer: "Total sales by referrer",
      todayLabel: "Today",
      customerName: "Customer name",
orderId: "Order ID",
yesterdayLabel: "Yesterday",
lastLabel: "Last",
quartersLabel: "Quarters",
customRangeLabel: "Custom range",
last24h: "Last 24 hours",
last7d: "Last 7 days",
last30d: "Last 30 days",
last90d: "Last 90 days",
lastYear: "Last year",
applyBtn: "Apply",
mainPeriod: "Main period",
comparisonPeriod: "Comparison period",
date: "Date",
reset: "Reset",
product: "Product",
cohort: "Cohort",
avgRetention: "Avg retention",
guest: "Guest",
unknown: "Unknown",
direct: "Direct",
ordersLabel: "orders",
unitsLabel: "units",
sessionsLabel: "sessions",
customersLabel: "customers",
returningLabel: "returning",
soldLabel: "sold",
leftLabel: "left",
from: "From",
to: "To",
notAllowed: "You are not allowed to view reports",
mobileTitle: "Reports are not available on mobile",
mobileDescription: "Please open this page from a desktop or large tablet screen.",
loading: "Loading...",
salesBreakdownGrossSales: "Gross sales",
salesBreakdownDiscounts: "Discounts",
salesBreakdownReturns: "Returns",
salesBreakdownNetSales: "Net sales",
errorTitle: "Something went wrong",
retry: "Retry",
emptyReportsTitle: "No reports found",
emptyReportsDescription: "Try changing your search keyword.",
emptyDetailsTitle: "No data available",
emptyDetailsDescription: "There is no data for the selected report and date range.",
salesBreakdownShipping: "Shipping",
salesBreakdownExtraFees: "Extra fees",
salesBreakdownTotalSales: "Total sales",
conversionSessions: "Sessions",
conversionAddedToCart: "Added to cart",
conversionReachedCheckout: "Reached checkout",
conversionCompleted: "Completed",
searchReports: "Search reports",
noReportsFound: "No reports found",
      performanceByReferringChannel: "Performance by referring channel",
      sessionsByReferrer: "Sessions by referrer",
      sellThrough: "Products by sell-through rate",
    },
    AR: {
      title: "التقارير",
      reports: "التقارير",
      name: "الاسم",
      category: "الفئة",
      product: "المنتج",
cohort: "المجموعة",
avgRetention: "متوسط الاحتفاظ",
guest: "زائر",
unknown: "غير معروف",
direct: "مباشر",
ordersLabel: "طلبات",
errorTitle: "حدث خطأ",
retry: "إعادة المحاولة",
emptyReportsTitle: "لا توجد تقارير",
emptyReportsDescription: "جرّب تغيير كلمة البحث.",
emptyDetailsTitle: "لا توجد بيانات",
emptyDetailsDescription: "لا توجد بيانات لهذا التقرير ضمن النطاق المحدد.",
unitsLabel: "قطع",
analytics: "التحليلات",
sessionsLabel: "جلسات",
customersLabel: "عملاء",
returningLabel: "عائدون",
soldLabel: "مباع",
leftLabel: "متبقي",
from: "من",
to: "إلى",
notAllowed: "غير مصرح لك بعرض التقارير",
mobileTitle: "التقارير غير متاحة على الهاتف",
mobileDescription: "يرجى فتح هذه الصفحة من شاشة كمبيوتر أو جهاز لوحي كبير.",
loading: "جاري التحميل...",
salesBreakdownGrossSales: "إجمالي الإيرادات",
salesBreakdownDiscounts: "الخصومات",
salesBreakdownReturns: "المرتجعات",
salesBreakdownNetSales: "صافي المبيعات",
salesBreakdownShipping: "الشحن",
salesBreakdownExtraFees: "الرسوم الإضافية",
salesBreakdownTotalSales: "إجمالي المبيعات",
conversionSessions: "الجلسات",
conversionAddedToCart: "أضيفت للسلة",
conversionReachedCheckout: "وصلت للدفع",
conversionCompleted: "مكتملة",
      customerName: "اسم العميل",
orderId: "رقم الطلب",
      createdBy: "أنشئ بواسطة",
      admin: "المدير",
      chooseExportFormat: "اختر صيغة التصدير",
exportAllPageDescription: "تصدير صفحة التقرير كاملة مع الرسم البياني والجداول.",
exportTablesDescription: "تصدير الجداول فقط بدون الرسم البياني.",
print: "طباعة",
pdf: "PDF",
csv: "CSV",
excel: "Excel",
backToExportOptions: "رجوع",
mainTable: "الجدول الأساسي",
comparisonTable: "جدول المقارنة",
      mainPeriod: "الفترة الأساسية",
comparisonPeriod: "فترة المقارنة",
date: "التاريخ",
total: "الإجمالي",
      today: "اليوم",
      noComparison: "بدون مقارنة",
      previousPeriod: "الفترة السابقة",
      previousYear: "السنة السابقة",
      currencyRateTitle: "تحديد سعر الصرف",
      usdToLbp: "1 دولار = كم ليرة",
      save: "حفظ",
      cancel: "إلغاء",
      todayLabel: "اليوم",
yesterdayLabel: "أمس",
lastLabel: "آخر",
quartersLabel: "الأرباع",
customRangeLabel: "نطاق مخصص",
last24h: "آخر 24 ساعة",
last7d: "آخر 7 أيام",
last30d: "آخر 30 يوم",
export: "تصدير",
exportReport: "تصدير التقرير",
exportAllPage: "تصدير الصفحة كاملة",
exportWithoutChart: "تصدير بدون الرسم البياني",
exportLaterNote: "سيتم تنفيذ وظيفة التصدير لاحقًا.",
last90d: "آخر 90 يوم",
lastYear: "آخر سنة",
applyBtn: "تطبيق",
reset: "إعادة تعيين",
searchReports: "البحث في التقارير",
noReportsFound: "لا توجد تقارير",
      back: "العودة إلى التقارير",
      details: "التفاصيل",
      noData: "لا توجد بيانات لهذا النطاق",
      mainDate: "التاريخ الأساسي",
      compareDate: "تاريخ المقارنة",
      value: "القيمة",
      comparison: "المقارنة",
      totalSalesOverTime: "إجمالي المبيعات عبر الوقت",
      grossSales: "إجمالي الإيرادات",
      returningRate: "نسبة العملاء العائدين",
      ordersFulfilled: "الطلبات المنجزة",
      totalOrders: "إجمالي الطلبات",
      totalSalesBreakdown: "تفصيل إجمالي المبيعات",
      salesByChannel: "إجمالي المبيعات حسب القناة",
      avgOrder: "متوسط قيمة الطلب عبر الوقت",
      salesByProduct: "إجمالي المبيعات حسب المنتج",
      sessionsOverTime: "الجلسات عبر الوقت",
      conversion: "معدل التحويل عبر الوقت",
      conversionBreakdown: "تفصيل معدل التحويل",
      topProducts: "المنتجات الأكثر مبيعًا",
      sessionsByDevice: "الجلسات حسب نوع الجهاز",
      sessionsByLocation: "الجلسات حسب الموقع",
      salesBySocial: "المبيعات حسب المصدر الاجتماعي",
      cohortAnalysis: "تحليل مجموعات العملاء",
      landingPage: "الجلسات حسب صفحة الدخول",
      sessionsBySocialReferrer: "الجلسات حسب المصدر الاجتماعي",
      salesByReferrer: "إجمالي المبيعات حسب المصدر",
      performanceByReferringChannel: "الأداء حسب قناة الإحالة",
      sessionsByReferrer: "الجلسات حسب المصدر",
      compareNone: "بدون مقارنة",
compareYesterday: "أمس | الفترة السابقة",
comparePrevYear: "السنة الماضية",
comparePrevYearMatch: "السنة الماضية (نفس يوم الأسبوع)",
compareCustom: "مخصص",
cancelBtn: "إلغاء",
selectedRange: "النطاق المحدد",
      sellThrough: "المنتجات حسب معدل البيع",
    },
  }[lang];

  const reportRows = useMemo(
    () => [
      { key: "totalSalesOverTime", name: TEXT.totalSalesOverTime, category: "sales" },
      { key: "grossSales", name: TEXT.grossSales, category: "finances" },
      { key: "returningRate", name: TEXT.returningRate, category: "sales" },
      { key: "ordersFulfilled", name: TEXT.ordersFulfilled, category: "orders" },
      { key: "totalOrders", name: TEXT.totalOrders, category: "orders" },
      { key: "totalSalesBreakdown", name: TEXT.totalSalesBreakdown, category: "finances" },
      { key: "salesByChannel", name: TEXT.salesByChannel, category: "sales" },
      { key: "avgOrder", name: TEXT.avgOrder, category: "sales" },
      { key: "salesByProduct", name: TEXT.salesByProduct, category: "sales" },
      { key: "sessionsOverTime", name: TEXT.sessionsOverTime, category: "Acquisition" },
      { key: "conversion", name: TEXT.conversion, category: "Acquisition" },
      { key: "conversionBreakdown", name: TEXT.conversionBreakdown, category: "Acquisition" },
      { key: "topProducts", name: TEXT.topProducts, category: "inventory" },
      { key: "sessionsByDevice", name: TEXT.sessionsByDevice, category: "Acquisition" },
      { key: "sessionsByLocation", name: TEXT.sessionsByLocation, category: "Acquisition" },
      { key: "salesBySocial", name: TEXT.salesBySocial, category: "sales" },
      { key: "cohortAnalysis", name: TEXT.cohortAnalysis, category: "sales" },
      { key: "landingPage", name: TEXT.landingPage, category: "Acquisition" },
      { key: "sessionsBySocialReferrer", name: TEXT.sessionsBySocialReferrer, category: "Acquisition" },
      { key: "salesByReferrer", name: TEXT.salesByReferrer, category: "sales" },
      { key: "performanceByReferringChannel", name: TEXT.performanceByReferringChannel, category: "Acquisition" },
      { key: "sessionsByReferrer", name: TEXT.sessionsByReferrer, category: "Acquisition" },
      { key: "sellThrough", name: TEXT.sellThrough, category: "inventory" },
    ],
    [TEXT]
  );

  useEffect(() => {
  const state = location.state;

  if (!state?.fromAnalytics || analyticsStateAppliedRef.current) return;

  const report = reportRows.find((row) => row.key === state.reportKey);
  if (!report) return;

  analyticsStateAppliedRef.current = true;

  const nextMainStart = state.mainStart
    ? parseChartDate(state.mainStart)
    : new Date();

  const nextMainEnd = state.mainEnd
    ? parseChartDate(state.mainEnd)
    : nextMainStart;

  const nextCompareRange =
    state.compareStart && state.compareEnd
      ? {
          start: parseChartDate(state.compareStart),
          end: parseChartDate(state.compareEnd),
        }
      : null;

  setSelectedReport(report);

  setMainStart(nextMainStart);
  setMainEnd(nextMainEnd);
  setMainRange({
    start: nextMainStart,
    end: nextMainEnd,
  });

  setRangeStart(nextMainStart);
  setRangeEnd(nextMainEnd);
  setSelectedDate(nextMainEnd);
  setCalendarBaseDate(nextMainEnd);
  setCalendarOffset(0);

  setCompareRange(nextCompareRange);
  setCompareType(state.compareType || (nextCompareRange ? "custom" : "none"));

  setCurrency(state.currency || "USD");

  if (state.exchangeRate) {
    setExchangeRate(Number(state.exchangeRate) || 89000);
  }

  loadCards(nextMainStart, nextMainEnd, nextCompareRange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.state, reportRows]);

  const applyRange = (start, end) => {
  setMainStart(start);
  setMainEnd(end);
  setMainRange({ start, end });

  setSelectedDate(end);
  setCalendarBaseDate(end);
  setCalendarOffset(0);
};

const resetToToday = () => {
  const today = new Date();

  setDateView("main");
  setDateTab("today");
  setActiveQuickRange(null);

  setRangeStart(null);
  setRangeEnd(null);

  setMainStart(today);
  setMainEnd(today);
  setMainRange(null);

  setSelectedDate(today);
  setCalendarBaseDate(today);
  setCalendarOffset(0);

  loadCards(today, today, compareRange);
};

const getLastQuarters = () => {
  const now = new Date();

  const currentQuarter = Math.floor(now.getMonth() / 3);
  const currentYear = now.getFullYear();

  const quarters = [];

  for (let i = 0; i < 4; i++) {
    let qIndex = currentQuarter - i;
    let year = currentYear;

    if (qIndex < 0) {
      qIndex += 4;
      year -= 1;
    }

    const startMonth = qIndex * 3;
    const endMonth = startMonth + 2;

    const start = new Date(year, startMonth, 1);
    const end = new Date(year, endMonth + 1, 0);

    quarters.push({
      label: `Q${qIndex + 1} ${year}`,
      start,
      end,
    });
  }

  return quarters;
};

  const loadCards = async (start = mainStart, end = mainEnd, compare = compareRange) => {
  try {
    setLoading(true);
    setLoadError("");

    const res = await getAdminAnalyticsCards({
      from: start ? formatLocalDate(start) : undefined,
      to: end ? formatLocalDate(end) : undefined,
      compareFrom: compare?.start ? formatLocalDate(compare.start) : undefined,
      compareTo: compare?.end ? formatLocalDate(compare.end) : undefined,
    });

    setCardsData(res);
  } catch (err) {
    console.error(err);
    setLoadError(
      lang === "AR"
        ? "تعذر تحميل بيانات التقارير. يرجى المحاولة مرة أخرى."
        : "Unable to load report data. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
  const start = mainStart || new Date();
  const end = mainEnd || start;

  let nextCompare = compareRange;

  if (compareType !== "custom") {
    nextCompare = buildCompareRange(compareType, start, end);
    setCompareRange(nextCompare);
  }

  loadCards(start, end, nextCompare);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [mainStart, mainEnd, compareType]);

useEffect(() => {
  const handleOutsideClick = (event) => {
    if (!event.target.closest(".compare-dropdown")) {
      setShowCompareDropdown(false);
    }
  };

  document.addEventListener("mousedown", handleOutsideClick);

  return () => {
    document.removeEventListener("mousedown", handleOutsideClick);
  };
}, []);

  const formatMoney = (value) => {
    const n = Number(value || 0);

    if (currency === "LBP") {
      return `${Math.round(n * exchangeRate).toLocaleString()} L.L`;
    }

    return `$${n.toFixed(2)}`;
  };

  const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

  const getMainButtonLabel = () => {
  if (!mainRange) {
    return lang === "AR" ? "اليوم" : "Today";
  }

  const { start, end } = mainRange;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const normalize = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const s = normalize(start);
  const e = normalize(end);

  const sameDay = s.getTime() === e.getTime();

  if (sameDay) {
    if (s.getTime() === today.getTime()) {
      return lang === "AR" ? "اليوم" : "Today";
    }

    if (s.getTime() === yesterday.getTime()) {
      return lang === "AR" ? "أمس" : "Yesterday";
    }

    return s
      .toLocaleDateString(lang === "AR" ? "ar" : "en", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  }

  return `${formatCompareDate(start).toUpperCase()} → ${formatCompareDate(end).toUpperCase()}`;
};

const formatCompareDate = (d) =>
  d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const getVisibleCalendarMonthLabel = () => {
  const base = calendarBaseDate || new Date();

  return new Date(
    base.getFullYear(),
    base.getMonth() + calendarOffset,
    1
  ).toLocaleString(lang === "AR" ? "ar" : "en", {
    month: "long",
    year: "numeric",
  });
};

const getDraftRangeLabel = (start, end) => {
  if (!start && !end) return "";

  if (start && !end) {
    return formatCompareDate(start);
  }

  if (start && end && start.toDateString() === end.toDateString()) {
    return formatCompareDate(start);
  }

  return `${formatCompareDate(start)} → ${formatCompareDate(end)}`;
};

const getSameWeekdayPreviousYear = (date) => {
  const current = new Date(date);
  const previous = new Date(date);

  previous.setFullYear(previous.getFullYear() - 1);

  const currentDay = current.getDay();
  const previousDay = previous.getDay();

  const diff = currentDay - previousDay;
  previous.setDate(previous.getDate() + diff);

  return previous;
};

const buildCompareRange = (type, start, end) => {
  if (!type || type === "none" || !start || !end) return null;

  const days = diffDaysInclusive(start, end);

  if (type === "yesterday") {
    return {
      start: addDays(start, -days),
      end: addDays(end, -days),
    };
  }

  if (type === "prevYear") {
    const s = new Date(start);
    const e = new Date(end);

    s.setFullYear(s.getFullYear() - 1);
    e.setFullYear(e.getFullYear() - 1);

    return { start: s, end: e };
  }

  if (type === "prevYearMatch") {
    const compareEnd = getSameWeekdayPreviousYear(end);
    const compareStart = addDays(compareEnd, -(days - 1));

    return {
      start: compareStart,
      end: compareEnd,
    };
  }

  return null;
};

const getDelta = (current, previous) => {
  current = Number(current || 0);
  previous = Number(previous || 0);

  if (current === 0 && previous === 0) {
    return {
      percent: null,
      direction: "same",
      hideComparison: true,
    };
  }

  if (previous === 0) {
    return {
      percent: null,
      direction: current > 0 ? "up" : "same",
      hideComparison: false,
      fromZero: current > 0,
    };
  }

  const percent = ((current - previous) / previous) * 100;

  if (Math.abs(percent) < 0.05) {
    return {
      percent: null,
      direction: "same",
      hideComparison: true,
    };
  }

  return {
    percent: Number(Math.abs(percent).toFixed(1)),
    direction: percent > 0 ? "up" : percent < 0 ? "down" : "same",
    hideComparison: false,
  };
};

  const getCompareButtonLabel = () => {
  if (!compareRange) {
    return lang === "AR" ? "بدون مقارنة" : "No comparison";
  }

  const { start, end } = compareRange;

  if (start.toDateString() === end.toDateString()) {
    return formatCompareDate(start).toUpperCase();
  }

  return `${formatCompareDate(start).toUpperCase()} → ${formatCompareDate(end).toUpperCase()}`;
};

  const applyPreviousPeriodCompare = () => {
    const start = mainStart || new Date();
    const end = mainEnd || start;
    const days = diffDaysInclusive(start, end);

    setCompareRange({
      start: addDays(start, -days),
      end: addDays(end, -days),
    });
  };

  const applyPreviousYearCompare = () => {
    const start = new Date(mainStart || new Date());
    const end = new Date(mainEnd || start);

    start.setFullYear(start.getFullYear() - 1);
    end.setFullYear(end.getFullYear() - 1);

    setCompareRange({ start, end });
  };

 const Calendar = ({
  monthOffset,
  selectedDate,
  calendarBaseDate,
  setSelectedDate,
  rangeStart,
  rangeEnd,
  setRangeStart,
  setRangeEnd,
  setCalendarOffset,
  activeQuickRange,
  setActiveQuickRange,
  isCompareCalendar = false,
  compact = false,
}) => {
  const isCustomMode = activeQuickRange === "custom";

  const safeSelected = selectedDate || new Date();
  const safeBaseDate = calendarBaseDate || safeSelected || new Date();

  const base = new Date(
    safeBaseDate.getFullYear(),
    safeBaseDate.getMonth() + monthOffset,
    1
  );

  const year = base.getFullYear();
  const month = base.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <div className={compact ? "w-[220px]" : "w-[260px]"}>
      <div className="grid grid-cols-7 text-xs text-gray-500 mb-1">
        {(lang === "AR"
          ? ["ح", "ن", "ث", "ر", "خ", "ج", "س"]
          : ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
        ).map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 text-sm">
        {days.map((d, i) => {
          const currentDate = d ? new Date(year, month, d) : null;

          const todayDate = new Date();
          todayDate.setHours(0, 0, 0, 0);

          const isFuture = currentDate && currentDate > todayDate;

          const isToday =
            d &&
            d === todayDate.getDate() &&
            month === todayDate.getMonth() &&
            year === todayDate.getFullYear();

          const isSelected =
            d &&
            !isFuture &&
            d === safeSelected.getDate() &&
            month === safeSelected.getMonth() &&
            year === safeSelected.getFullYear();

          const start =
            rangeStart && rangeEnd && rangeStart > rangeEnd
              ? rangeEnd
              : rangeStart;

          const end =
            rangeStart && rangeEnd && rangeStart > rangeEnd
              ? rangeStart
              : rangeEnd;

          const isRangeStart =
            currentDate &&
            rangeStart &&
            currentDate.toDateString() === rangeStart.toDateString();

          const isRangeEnd =
            currentDate &&
            rangeEnd &&
            currentDate.toDateString() === rangeEnd.toDateString();

          const isInRange =
            currentDate && start && end && currentDate > start && currentDate < end;

          return (
            <button
              type="button"
              key={i}
              onClick={() => {
                if (!d || isFuture) return;

                const clicked = new Date(year, month, d);

                if (activeQuickRange && activeQuickRange !== "custom") {
                  setActiveQuickRange(null);
                }

                if (isCompareCalendar) {
                  const isSingleCompareDay =
                    rangeStart &&
                    rangeEnd &&
                    rangeStart.toDateString() === rangeEnd.toDateString();

                  if (!rangeStart || !rangeEnd || !isSingleCompareDay) {
                    setRangeStart(clicked);
                    setRangeEnd(clicked);
                    setSelectedDate(clicked);
                    return;
                  }

                  if (clicked < rangeStart) {
                    setRangeStart(clicked);
                    setRangeEnd(rangeStart);
                  } else {
                    setRangeStart(rangeStart);
                    setRangeEnd(clicked);
                  }

                  setSelectedDate(clicked);
                  return;
                }

                if (isCustomMode) {
                  if (!rangeStart || (rangeStart && rangeEnd)) {
                    setRangeStart(clicked);
                    setRangeEnd(null);
                    setSelectedDate(clicked);
                    return;
                  }

                  if (!rangeEnd) {
                    if (clicked < rangeStart) {
                      setRangeEnd(rangeStart);
                      setRangeStart(clicked);
                    } else {
                      setRangeEnd(clicked);
                    }

                    setSelectedDate(clicked);
                    return;
                  }
                }

                setRangeStart(clicked);
                setRangeEnd(clicked);
                setSelectedDate(clicked);
                applyRange(clicked, clicked);
              }}
              className={`
                ${compact ? "h-7 w-7 text-[12px]" : "h-8 w-8 text-[13px]"}
                flex items-center justify-center
                rounded-md
                transition
                ${isFuture ? "text-gray-300 cursor-not-allowed" : "hover:bg-gray-200"}
                ${isInRange ? "bg-gray-200" : ""}
                ${isRangeStart || isRangeEnd ? "bg-black text-white" : ""}
              `}
            >
              <div className="relative flex items-center justify-center w-full h-full">
                <span className="leading-none">{d}</span>

                {isToday && !isSelected && !isRangeStart && !isRangeEnd && (
                  <span className="absolute bottom-1 w-1 h-1 bg-black rounded-full" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const formatHourLabel = (hour) => {
  const h = Number(hour || 0);
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  const suffix = h < 12 ? "AM" : "PM";

  return `${displayHour} ${suffix}`;
};

const formatReportRowDate = (value, source = "main") => {
  return formatReportDateTime({ date: value }, source);
};

const formatReportDateTime = (point, source = "main") => {
  const d = parseChartDate(point?.date);

  const isOneDay =
    source === "comparison"
      ? getComparisonRangeDaysForChart() === 1
      : getRangeDaysForChart() === 1;

  const dateLabel = d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  if (!isOneDay) {
    return dateLabel;
  }

  const hour =
    point?.hour !== undefined
      ? Number(point.hour || 0)
      : d.getHours();

  return `${dateLabel} ${formatHourLabel(hour)}`;
};

const getRangeDaysForChart = () => {
  const start = mainStart || new Date();
  const end = mainEnd || start;

  return diffDaysInclusive(start, end);
};

const getComparisonRangeDaysForChart = () => {
  if (!cardsData?.comparison?.from || !cardsData?.comparison?.to) {
    return 0;
  }

  return diffDaysInclusive(
    parseChartDate(cardsData.comparison.from),
    parseChartDate(cardsData.comparison.to)
  );
};

const normalizeComparisonDailyTrend = (
  trend = [],
  from,
  to,
  granularity,
  valueKeys = []
) => {
  if (granularity !== "day" || !from || !to) {
    return trend || [];
  }

  const map = new Map(
    (trend || []).map((point) => [
      formatLocalDate(parseChartDate(point.date)),
      point,
    ])
  );

  const result = [];
  const cursor = parseChartDate(from);
  const last = parseChartDate(to);

  cursor.setHours(0, 0, 0, 0);
  last.setHours(0, 0, 0, 0);

  while (cursor <= last) {
    const key = formatLocalDate(cursor);

    if (map.has(key)) {
      result.push(map.get(key));
    } else {
      const emptyPoint = { date: key };

      valueKeys.forEach((keyName) => {
        emptyPoint[keyName] = 0;
      });

      result.push(emptyPoint);
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
};

const normalizeComparisonTrendToMainLength = (
  trend = [],
  from,
  to,
  granularity,
  valueKeys = [],
  targetLength = 0
) => {
  const normalized = normalizeComparisonDailyTrend(
    trend,
    from,
    to,
    granularity,
    valueKeys
  );

  if (granularity !== "day" || !targetLength) {
    return normalized;
  }

  if (normalized.length === targetLength) {
    return normalized;
  }

  if (normalized.length > targetLength) {
    return normalized.slice(0, targetLength);
  }

  const result = [...normalized];

  const cursor =
    result.length > 0
      ? parseChartDate(result[result.length - 1].date)
      : parseChartDate(from);

  cursor.setHours(0, 0, 0, 0);

  if (result.length > 0) {
    cursor.setDate(cursor.getDate() + 1);
  }

  while (result.length < targetLength) {
    const key = formatLocalDate(cursor);
    const emptyPoint = { date: key };

    valueKeys.forEach((keyName) => {
      emptyPoint[keyName] = 0;
    });

    result.push(emptyPoint);
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
};

const getReportPointValue = (point, key) => {
  if (!point) return 0;

  switch (key) {
  case "totalSalesOverTime":
  case "grossSales":
    return Number(point.sales || 0);

    case "avgOrder":
      return Number(point.averageOrderValue || 0);

    case "sessionsOverTime":
      return Number(point.sessions || 0);

    case "conversion":
      return Number(point.conversionRate || 0);

    default:
      return 0;
  }
};

const getReportTrendConfig = (key) => {
  if (!cardsData) {
    return {
      mainTrend: [],
      compareTrend: [],
      mainGranularity: "day",
      compareGranularity: "day",
      valueKeys: [],
    };
  }

  if (key === "totalSalesOverTime" || key === "grossSales") {
  return {
    mainTrend: cardsData.trend || [],
    compareTrend: cardsData.comparison?.trend || [],
    mainGranularity: cardsData.trendGranularity || "day",
    compareGranularity:
      cardsData.comparison?.trendGranularity ||
      cardsData.trendGranularity ||
      "day",
    valueKeys: ["sales", "orders", "customers", "fulfilledOrders"],
  };
}

  if (key === "avgOrder") {
    return {
      mainTrend: cardsData.averageOrderValueTrend || [],
      compareTrend: cardsData.comparison?.averageOrderValueTrend || [],
      mainGranularity: cardsData.averageOrderValueTrendGranularity || "day",
      compareGranularity:
        cardsData.comparison?.averageOrderValueTrendGranularity ||
        cardsData.averageOrderValueTrendGranularity ||
        "day",
      valueKeys: ["averageOrderValue", "orders"],
    };
  }

  if (key === "sessionsOverTime") {
    return {
      mainTrend: cardsData.sessionsTrend || [],
      compareTrend: cardsData.comparison?.sessionsTrend || [],
      mainGranularity: cardsData.sessionsTrendGranularity || "day",
      compareGranularity:
        cardsData.comparison?.sessionsTrendGranularity ||
        cardsData.sessionsTrendGranularity ||
        "day",
      valueKeys: ["sessions"],
    };
  }

  if (key === "conversion") {
    return {
      mainTrend: cardsData.conversionRateTrend || [],
      compareTrend: cardsData.comparison?.conversionRateTrend || [],
      mainGranularity: cardsData.conversionRateTrendGranularity || "day",
      compareGranularity:
        cardsData.comparison?.conversionRateTrendGranularity ||
        cardsData.conversionRateTrendGranularity ||
        "day",
      valueKeys: ["conversionRate", "activeSessions", "convertedSessions"],
    };
  }

  return {
    mainTrend: [],
    compareTrend: [],
    mainGranularity: "day",
    compareGranularity: "day",
    valueKeys: [],
  };
};

const formatReportAxisLabel = (point, index, trend, granularity) => {
  if (!point) return "";

  if (granularity === "hour") {
    const hour =
      point.hour !== undefined
        ? Number(point.hour || 0)
        : parseChartDate(point.date).getHours();

    return hour % 3 === 0 ? formatHourLabel(hour) : "";
  }

  const d = parseChartDate(point.date);
  const rangeDays = getRangeDaysForChart();

  if (rangeDays > 30) {
    const previous = index > 0 ? parseChartDate(trend[index - 1]?.date) : null;

    if (
      previous &&
      previous.getMonth() === d.getMonth() &&
      previous.getFullYear() === d.getFullYear()
    ) {
      return "";
    }

    return d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      month: "short",
      ...(rangeDays > 365 ? { year: "numeric" } : {}),
    });
  }

  if (rangeDays <= 15) {
    return d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "short",
    });
  }

  if (index === 0 || index === trend.length - 1 || index % 2 === 0) {
    return d.toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "short",
    });
  }

  return "";
};

const formatReportTooltipTitle = (point) => {
  if (!point) return "";

  return formatReportDateTime(point);
};

const truncateChartLabel = (value, max = 18) => {
  const text = String(value || "");

  if (text.length <= max) return text;

  return `${text.slice(0, max)}...`;
};

const getMoneyChartValue = (value) => Number(value || 0);

const getNumberChartValue = (value) => Number(value || 0);

const getPercentChartValue = (value) => Number(value || 0);

const getChartValueFormatter = (valueType) => {
  if (valueType === "money") {
    return (value) => formatMoney(value);
  }

  if (valueType === "percent") {
    return (value) => formatPercent(value);
  }

  return (value) => Number(value || 0).toLocaleString();
};

const getChartYAxisLabel = (valueType) => {
  if (valueType === "money") {
    return currency === "LBP" ? "L.L" : "$";
  }

  if (valueType === "percent") {
    return "%";
  }

  return "";
};

const makeBarChartData = ({
  labels = [],
  values = [],
  compareValues = [],
  valueType = "number",
  label = selectedReport?.name || "",
}) => {
  const hasCompare =
    Boolean(cardsData?.comparison) &&
    Array.isArray(compareValues) &&
    compareValues.length > 0;

  return {
    type: "bar",
    valueType,
    labels: labels.map((item) => truncateChartLabel(item)),
    originalLabels: labels,
    datasets: [
      {
        label,
        data: values.map((v) => Number(v || 0)),
        backgroundColor: "rgba(14,165,233,0.72)",
        borderColor: "#0EA5E9",
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 44,
      },
      ...(hasCompare
        ? [
            {
              label: TEXT.comparison,
              data: compareValues.map((v) => Number(v || 0)),
              backgroundColor: "rgba(156,163,175,0.72)",
              borderColor: "#9CA3AF",
              borderWidth: 1,
              borderRadius: 8,
              maxBarThickness: 44,
            },
          ]
        : []),
    ],
  };
};

const alignCompareByKey = (mainItems = [], compareItems = [], getKey, getValue) => {
  const compareMap = new Map(
    (compareItems || []).map((item) => [getKey(item), getValue(item)])
  );

  return (mainItems || []).map((item) => Number(compareMap.get(getKey(item)) || 0));
};

const getCategoricalChartData = (key) => {
  if (!cardsData) return null;

  switch (key) {
    case "returningRate":
      return makeBarChartData({
        labels: [TEXT.returningRate],
        values: [getPercentChartValue(cardsData.returningRate)],
        compareValues: cardsData.comparison
          ? [getPercentChartValue(cardsData.comparison.returningRate)]
          : [],
        valueType: "percent",
      });

    case "ordersFulfilled":
      return makeBarChartData({
        labels: [TEXT.ordersFulfilled],
        values: [getNumberChartValue(cardsData.fulfilledOrders)],
        compareValues: cardsData.comparison
          ? [getNumberChartValue(cardsData.comparison.fulfilledOrders)]
          : [],
        valueType: "number",
      });

    case "totalOrders":
      return makeBarChartData({
        labels: [TEXT.totalOrders],
        values: [getNumberChartValue(cardsData.totalOrders)],
        compareValues: cardsData.comparison
          ? [getNumberChartValue(cardsData.comparison.totalOrders)]
          : [],
        valueType: "number",
      });

    case "totalSalesBreakdown": {
      const main = cardsData.salesBreakdown || {};
      const compare = cardsData.comparison?.salesBreakdown || {};

      const items = [
  [TEXT.salesBreakdownGrossSales, "grossSales"],
  [TEXT.salesBreakdownDiscounts, "discounts"],
  [TEXT.salesBreakdownReturns, "returns"],
  [TEXT.salesBreakdownNetSales, "netSales"],
  [TEXT.salesBreakdownShipping, "shippingCharges"],
  [TEXT.salesBreakdownExtraFees, "additionalFees"],
  [TEXT.salesBreakdownTotalSales, "totalSales"],
];

      return makeBarChartData({
        labels: items.map(([label]) => label),
        values: items.map(([, field]) => getMoneyChartValue(main[field])),
        compareValues: cardsData.comparison
          ? items.map(([, field]) => getMoneyChartValue(compare[field]))
          : [],
        valueType: "money",
      });
    }

    case "salesByChannel": {
      const items = cardsData.salesByChannel || [];

      return makeBarChartData({
        labels: items.map((item) => item.channel || TEXT.unknown),
        values: items.map((item) => getMoneyChartValue(item.totalSales)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.salesByChannel || [],
          (item) => item.channel,
          (item) => item.totalSales
        ),
        valueType: "money",
      });
    }

    case "salesByProduct": {
      const items = cardsData.salesByProduct || [];

      return makeBarChartData({
        labels: items.map((item) =>
          lang === "AR" ? item.name_ar : item.name_en
        ),
        values: items.map((item) => getMoneyChartValue(item.totalSales)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.salesByProduct || [],
          (item) => String(item.productId || item.publicId || item.name_en || item.name_ar),
          (item) => item.totalSales
        ),
        valueType: "money",
      });
    }

    case "conversionBreakdown": {
      const main = cardsData.conversionBreakdown || {};
      const compare = cardsData.comparison?.conversionBreakdown || {};

      const items = [
  [TEXT.conversionSessions, "sessions"],
  [TEXT.conversionAddedToCart, "addedToCart"],
  [TEXT.conversionReachedCheckout, "reachedCheckout"],
  [TEXT.conversionCompleted, "completed"],
];

      return makeBarChartData({
        labels: items.map(([label]) => label),
        values: items.map(([, field]) => getNumberChartValue(main[field])),
        compareValues: cardsData.comparison
          ? items.map(([, field]) => getNumberChartValue(compare[field]))
          : [],
        valueType: "number",
      });
    }

    case "topProducts": {
      const items = cardsData.topSellingProducts || [];

      return makeBarChartData({
        labels: items.map((item) =>
          lang === "AR" ? item.name_ar : item.name_en
        ),
        values: items.map((item) => getNumberChartValue(item.unitsSold)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.topSellingProducts || [],
          (item) => String(item.productId || item.publicId || item.name_en || item.name_ar),
          (item) => item.unitsSold
        ),
        valueType: "number",
      });
    }

    case "sessionsByDevice": {
      const items = cardsData.sessionsByDevice || [];

      return makeBarChartData({
        labels: items.map((item) => item.label || item.device || TEXT.unknown),
        values: items.map((item) => getNumberChartValue(item.sessions)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.sessionsByDevice || [],
          (item) => item.device,
          (item) => item.sessions
        ),
        valueType: "number",
      });
    }

    case "sessionsByLocation": {
      const items = cardsData.sessionsByLocation || [];

      return makeBarChartData({
        labels: items.map((item) => item.country || item.label || TEXT.unknown),
        values: items.map((item) => getNumberChartValue(item.sessions)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.sessionsByLocation || [],
          (item) => item.country,
          (item) => item.sessions
        ),
        valueType: "number",
      });
    }

    case "salesBySocial": {
      const items = cardsData.salesBySocialReferrer || [];

      return makeBarChartData({
        labels: items.map((item) => item.referrer || TEXT.unknown),
        values: items.map((item) => getMoneyChartValue(item.totalSales)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.salesBySocialReferrer || [],
          (item) => item.referrer,
          (item) => item.totalSales
        ),
        valueType: "money",
      });
    }

    case "landingPage": {
      const items = cardsData.sessionsByLandingPage || [];

      return makeBarChartData({
        labels: items.map((item) => item.label || item.landingPage || TEXT.unknown),
        values: items.map((item) => getNumberChartValue(item.sessions)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.sessionsByLandingPage || [],
          (item) => item.landingPage,
          (item) => item.sessions
        ),
        valueType: "number",
      });
    }

    case "sessionsBySocialReferrer": {
      const items = cardsData.sessionsBySocialReferrer || [];

      return makeBarChartData({
        labels: items.map((item) => item.referrer || TEXT.unknown),
        values: items.map((item) => getNumberChartValue(item.sessions)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.sessionsBySocialReferrer || [],
          (item) => item.referrer,
          (item) => item.sessions
        ),
        valueType: "number",
      });
    }

    case "salesByReferrer": {
      const items = cardsData.salesByReferrer || [];

      return makeBarChartData({
        labels: items.map((item) => item.referrer || TEXT.direct),
        values: items.map((item) => getMoneyChartValue(item.totalSales)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.salesByReferrer || [],
          (item) => item.referrer,
          (item) => item.totalSales
        ),
        valueType: "money",
      });
    }

    case "performanceByReferringChannel": {
      const items = cardsData.performanceByReferringChannel || [];

      return makeBarChartData({
        labels: items.map((item) => item.channel || "direct"),
        values: items.map((item) => getMoneyChartValue(item.totalSales)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.performanceByReferringChannel || [],
          (item) => item.channel,
          (item) => item.totalSales
        ),
        valueType: "money",
      });
    }

    case "sessionsByReferrer": {
      const items = cardsData.sessionsByReferrer || [];

      return makeBarChartData({
        labels: items.map((item) => item.referrer || TEXT.direct),
        values: items.map((item) => getNumberChartValue(item.sessions)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.sessionsByReferrer || [],
          (item) => item.referrer,
          (item) => item.sessions
        ),
        valueType: "number",
      });
    }

    case "sellThrough": {
      const items = cardsData.productsBySellThroughRate || [];

      return makeBarChartData({
        labels: items.map((item) =>
          lang === "AR" ? item.name_ar : item.name_en
        ),
        values: items.map((item) => getPercentChartValue(item.sellThroughRate)),
        compareValues: alignCompareByKey(
          items,
          cardsData.comparison?.productsBySellThroughRate || [],
          (item) => String(item.productId || item.publicId || item.name_en || item.name_ar),
          (item) => item.sellThroughRate
        ),
        valueType: "percent",
      });
    }

    case "cohortAnalysis": {
      const items = cardsData.customerCohorts || [];

      return makeBarChartData({
        labels: items.map((item) => item.label || item.cohort),
        values: items.map((item) => {
          const cells = item.cells || [];
          const rates = cells.map((cell) => Number(cell.retentionRate || 0));
          if (!rates.length) return 0;

          return rates.reduce((sum, v) => sum + v, 0) / rates.length;
        }),
        compareValues: [],
        valueType: "percent",
      });
    }

    default:
      return null;
  }
};

  const buildChartData = (key) => {
  if (!cardsData) return null;

  const {
  mainTrend,
  compareTrend,
  mainGranularity,
  compareGranularity,
  valueKeys,
} = getReportTrendConfig(key);

if (!mainTrend.length) {
  return getCategoricalChartData(key);
}

  const normalizedCompareTrend = normalizeComparisonTrendToMainLength(
    compareTrend,
    cardsData.comparison?.from,
    cardsData.comparison?.to,
    compareGranularity,
    valueKeys,
    mainTrend.length
  );

  const mainRangeDays = getRangeDaysForChart();
  const compareRangeDays = getComparisonRangeDaysForChart();

  const canOverlayCompare =
    Boolean(cardsData?.comparison) &&
    normalizedCompareTrend.length > 0 &&
    compareGranularity === mainGranularity &&
    (
      (mainGranularity === "hour" &&
        mainRangeDays === 1 &&
        compareRangeDays === 1) ||
      (mainGranularity === "day" &&
        mainRangeDays > 1 &&
        compareRangeDays === mainRangeDays)
    );

  const labels = mainTrend.map((point, index) =>
    formatReportAxisLabel(point, index, mainTrend, mainGranularity)
  );

  const mainValues = mainTrend.map((point) => getReportPointValue(point, key));

  const compareValues = canOverlayCompare
    ? labels.map((_, index) =>
        getReportPointValue(normalizedCompareTrend[index], key)
      )
    : [];

  const datasets = [
    {
      label: selectedReport?.name || key,
      data: mainValues,
      borderColor: "#0EA5E9",
      backgroundColor: "rgba(14,165,233,0.10)",
      pointBackgroundColor: "#0EA5E9",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 3,
      tension: 0.35,
      fill: true,
    },
  ];

  if (canOverlayCompare) {
    datasets.push({
      label: TEXT.comparison,
      data: compareValues,
      borderColor: "#9CA3AF",
      backgroundColor: "transparent",
      pointBackgroundColor: "#9CA3AF",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 2,
      tension: 0.35,
      fill: false,
    });
  }

  return {
  type: "line",
  valueType:
    key === "conversion"
      ? "percent"
      : key === "sessionsOverTime"
      ? "number"
      : "money",
  labels,
  datasets,
  _mainTrend: mainTrend,
  _compareTrend: canOverlayCompare ? normalizedCompareTrend : [],
  _mainGranularity: mainGranularity,
};
};

const formatConversionBreakdownRowValue = (label, value) => {
  const percentFields = [
    "sessionsRate",
    "addedToCartRate",
    "reachedCheckoutRate",
    "completedRate",
  ];

  if (percentFields.includes(label)) {
    return formatPercent(value);
  }

  return typeof value === "number" ? value : String(value);
};

  const getDetailRows = () => {
    if (!cardsData || !selectedReport) return [];

    switch (selectedReport.key) {
      case "totalSalesOverTime":
  return (cardsData.trend || []).map((p) => ({
    label: formatReportDateTime(p),
    value: formatMoney(p.sales),
    extra: `${p.orders || 0} ${TEXT.ordersLabel}`,
  }));

     case "grossSales":
  return [
    {
      label:
        (mainStart || new Date()).toDateString() ===
        (mainEnd || mainStart || new Date()).toDateString()
          ? formatCompareDate(mainStart || new Date())
          : `${formatCompareDate(mainStart || new Date())} → ${formatCompareDate(
              mainEnd || mainStart || new Date()
            )}`,
      value: formatMoney(cardsData.grossSales),
      extra: "—",
    },
  ];

      case "returningRate":
  return (cardsData.returningCustomerOrders || []).map((item) => ({
    label: formatReportRowDate(item.date),
    value: item.customerName || TEXT.guest,
    extra: formatMoney(item.totalAmount),
  }));

case "ordersFulfilled":
  return (cardsData.fulfilledOrderDetails || []).map((item) => ({
    label: formatReportRowDate(item.date),
    value: item.orderNumber || `#${item.id}`,
    extra: formatMoney(item.totalAmount),
  }));

case "totalOrders":
  return (cardsData.orderDetails || []).map((item) => ({
    label: formatReportRowDate(item.date),
    value: item.orderNumber || `#${item.id}`,
    extra: formatMoney(item.totalAmount),
  }));

      case "totalSalesBreakdown":
        return Object.entries(cardsData.salesBreakdown || {}).map(([label, value]) => ({
          label,
          value: formatMoney(value),
        }));

      case "salesByChannel":
        return (cardsData.salesByChannel || []).map((item) => ({
          label: item.channel,
          value: formatMoney(item.totalSales),
          extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
        }));

      case "avgOrder":
  return (cardsData.averageOrderValueTrend || []).map((p) => ({
    label: formatReportDateTime(p),
    value: formatMoney(p.averageOrderValue),
    extra: `${p.orders || 0} ${TEXT.ordersLabel}`,
  }));

      case "salesByProduct":
        return (cardsData.salesByProduct || []).map((item) => ({
          label: lang === "AR" ? item.name_ar : item.name_en,
          value: formatMoney(item.totalSales),
          extra: `${item.unitsSold || 0} ${TEXT.unitsLabel}`,
        }));

      case "sessionsOverTime":
  return (cardsData.sessionsTrend || []).map((p) => ({
    label: formatReportDateTime(p),
    value: p.sessions || 0,
  }));

      case "conversion":
  return (cardsData.conversionRateTrend || []).map((p) => ({
    label: formatReportDateTime(p),
    value: formatPercent(p.conversionRate),
    extra: `${p.convertedSessions || 0}/${p.activeSessions || 0}`,
  }));

      case "conversionBreakdown":
  return Object.entries(cardsData.conversionBreakdown || {}).map(
    ([label, value]) => ({
      label,
      value: formatConversionBreakdownRowValue(label, value),
    })
  );

     case "topProducts":
  return (cardsData.topSellingProducts || []).map((item) => ({
    label: lang === "AR" ? item.name_ar : item.name_en,
    value: `${item.unitsSold || 0} ${TEXT.unitsLabel}`,
    extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
  }));

      case "sessionsByDevice":
        return (cardsData.sessionsByDevice || []).map((item) => ({
          label: item.label || item.device,
          value: item.sessions,
          extra: `${item.percent}%`,
        }));

      case "sessionsByLocation":
        return (cardsData.sessionsByLocation || []).map((item) => ({
          label: item.country,
          value: item.sessions,
          extra: `${item.percent}%`,
        }));

      case "salesBySocial":
        return (cardsData.salesBySocialReferrer || []).map((item) => ({
          label: item.referrer,
          value: formatMoney(item.totalSales),
          extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
        }));

      case "landingPage":
        return (cardsData.sessionsByLandingPage || []).map((item) => ({
          label: item.label || item.landingPage,
          value: item.sessions,
          extra: `${item.percent}%`,
        }));

      case "sessionsBySocialReferrer":
        return (cardsData.sessionsBySocialReferrer || []).map((item) => ({
          label: item.referrer,
          value: item.sessions,
          extra: `${item.percent}%`,
        }));

      case "salesByReferrer":
        return (cardsData.salesByReferrer || []).map((item) => ({
          label: item.referrer,
          value: formatMoney(item.totalSales),
          extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
        }));

      case "performanceByReferringChannel":
        return (cardsData.performanceByReferringChannel || []).map((item) => ({
          label: item.channel,
          value: formatMoney(item.totalSales),
          extra: `${item.sessions || 0} ${TEXT.sessionsLabel} / ${item.orders || 0} ${TEXT.ordersLabel}`,
        }));

      case "sessionsByReferrer":
        return (cardsData.sessionsByReferrer || []).map((item) => ({
          label: item.referrer,
          value: item.sessions,
          extra: `${item.percent}%`,
        }));

      case "sellThrough":
        return (cardsData.productsBySellThroughRate || []).map((item) => ({
          label: lang === "AR" ? item.name_ar : item.name_en,
          value: `${item.sellThroughRate}%`,
          extra: `${item.unitsSold || 0} ${TEXT.soldLabel} / ${item.endingInventory || 0} ${TEXT.leftLabel}`,
        }));

        case "cohortAnalysis":
  return (cardsData.customerCohorts || []).map((item) => {
    const cells = item.cells || [];

    const avgRetention =
      cells.length > 0
        ? cells.reduce(
            (sum, cell) => sum + Number(cell.retentionRate || 0),
            0
          ) / cells.length
        : 0;

    const returningCustomers = cells.reduce(
      (sum, cell) => sum + Number(cell.customers || 0),
      0
    );

    return {
      label: item.label || item.cohort,
      value: formatPercent(avgRetention),
      extra: `${item.customers || 0} ${TEXT.customersLabel} / ${returningCustomers} ${TEXT.returningLabel}`,
    };
  });

      default:
        return [];
    }
  };

  const getComparisonDetailRows = () => {
  if (!cardsData?.comparison || !selectedReport) return [];

  switch (selectedReport.key) {
    case "totalSalesOverTime":
      return (cardsData.comparison.trend || []).map((p) => ({
        label: formatReportDateTime(p, "comparison"),
        value: formatMoney(p.sales),
        extra: `${p.orders || 0} ${TEXT.ordersLabel}`,
      }));

    case "avgOrder":
      return (cardsData.comparison.averageOrderValueTrend || []).map((p) => ({
        label: formatReportDateTime(p, "comparison"),
        value: formatMoney(p.averageOrderValue),
        extra: `${p.orders || 0} ${TEXT.ordersLabel}`,
      }));

    case "sessionsOverTime":
      return (cardsData.comparison.sessionsTrend || []).map((p) => ({
        label: formatReportDateTime(p, "comparison"),
        value: p.sessions || 0,
      }));

    case "conversion":
      return (cardsData.comparison.conversionRateTrend || []).map((p) => ({
        label: formatReportDateTime(p, "comparison"),
        value: formatPercent(p.conversionRate),
        extra: `${p.convertedSessions || 0}/${p.activeSessions || 0}`,
      }));

    case "grossSales":
  return [
    {
      label:
        cardsData.comparison.from && cardsData.comparison.to
          ? parseChartDate(cardsData.comparison.from).toDateString() ===
            parseChartDate(cardsData.comparison.to).toDateString()
            ? formatCompareDate(parseChartDate(cardsData.comparison.from))
            : `${formatCompareDate(parseChartDate(cardsData.comparison.from))} → ${formatCompareDate(
                parseChartDate(cardsData.comparison.to)
              )}`
          : TEXT.comparisonPeriod,
      value: formatMoney(cardsData.comparison.grossSales),
      extra: "—",
    },
  ];

   case "returningRate":
  return (cardsData.comparison.returningCustomerOrders || []).map((item) => ({
    label: formatReportRowDate(item.date, "comparison"),
    value: item.customerName || TEXT.guest,
    extra: formatMoney(item.totalAmount),
  }));

case "ordersFulfilled":
  return (cardsData.comparison.fulfilledOrderDetails || []).map((item) => ({
    label: formatReportRowDate(item.date, "comparison"),
    value: item.orderNumber || `#${item.id}`,
    extra: formatMoney(item.totalAmount),
  }));

case "totalOrders":
  return (cardsData.comparison.orderDetails || []).map((item) => ({
    label: formatReportRowDate(item.date, "comparison"),
    value: item.orderNumber || `#${item.id}`,
    extra: formatMoney(item.totalAmount),
  }));

  case "totalSalesBreakdown":
  return Object.entries(cardsData.comparison.salesBreakdown || {}).map(
    ([label, value]) => ({
      label,
      value: formatMoney(value),
    })
  );

case "salesByChannel":
  return (cardsData.comparison.salesByChannel || []).map((item) => ({
    label: item.channel,
    value: formatMoney(item.totalSales),
    extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
  }));

case "salesByProduct":
  return (cardsData.comparison.salesByProduct || []).map((item) => ({
    label: lang === "AR" ? item.name_ar : item.name_en,
    value: formatMoney(item.totalSales),
    extra: `${item.unitsSold || 0} ${TEXT.unitsLabel}`,
  }));

case "conversionBreakdown":
  return Object.entries(cardsData.comparison.conversionBreakdown || {}).map(
    ([label, value]) => ({
      label,
      value: formatConversionBreakdownRowValue(label, value),
    })
  );

  case "topProducts":
  return (cardsData.comparison.topSellingProducts || []).map((item) => ({
    label: lang === "AR" ? item.name_ar : item.name_en,
    value: `${item.unitsSold || 0} ${TEXT.unitsLabel}`,
    extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
  }));

  case "sessionsByDevice":
  return (cardsData.comparison.sessionsByDevice || []).map((item) => ({
    label: item.label || item.device,
    value: item.sessions,
    extra: `${item.percent}%`,
  }));

case "sessionsByLocation":
  return (cardsData.comparison.sessionsByLocation || []).map((item) => ({
    label: item.country,
    value: item.sessions,
    extra: `${item.percent}%`,
  }));

case "salesBySocial":
  return (cardsData.comparison.salesBySocialReferrer || []).map((item) => ({
    label: item.referrer,
    value: formatMoney(item.totalSales),
    extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
  }));

case "landingPage":
  return (cardsData.comparison.sessionsByLandingPage || []).map((item) => ({
    label: item.label || item.landingPage,
    value: item.sessions,
    extra: `${item.percent}%`,
  }));

case "sessionsBySocialReferrer":
  return (cardsData.comparison.sessionsBySocialReferrer || []).map((item) => ({
    label: item.referrer,
    value: item.sessions,
    extra: `${item.percent}%`,
  }));

case "salesByReferrer":
  return (cardsData.comparison.salesByReferrer || []).map((item) => ({
    label: item.referrer,
    value: formatMoney(item.totalSales),
    extra: `${item.orders || 0} ${TEXT.ordersLabel}`,
  }));

case "performanceByReferringChannel":
  return (cardsData.comparison.performanceByReferringChannel || []).map(
    (item) => ({
      label: item.channel,
      value: formatMoney(item.totalSales),
      extra: `${item.sessions || 0} ${TEXT.sessionsLabel} / ${item.orders || 0} ${TEXT.ordersLabel}`,
    })
  );

case "sessionsByReferrer":
  return (cardsData.comparison.sessionsByReferrer || []).map((item) => ({
    label: item.referrer,
    value: item.sessions,
    extra: `${item.percent}%`,
  }));

case "sellThrough":
  return (cardsData.comparison.productsBySellThroughRate || []).map((item) => ({
    label: lang === "AR" ? item.name_ar : item.name_en,
    value: `${item.sellThroughRate}%`,
    extra: `${item.unitsSold || 0} ${TEXT.soldLabel} / ${item.endingInventory || 0} ${TEXT.leftLabel}`,
  }));

    default:
      return [];
  }
};

 const chartData = selectedReport ? buildChartData(selectedReport.key) : null;
const detailRows = getDetailRows();
const comparisonDetailRows = getComparisonDetailRows();

  const filteredReportRows = reportRows.filter((row) => {
  const q = reportSearch.trim().toLowerCase();

  if (!q) return true;

  return (
    row.name.toLowerCase().includes(q) ||
    row.category.toLowerCase().includes(q) ||
    TEXT.admin.toLowerCase().includes(q)
  );
});

const getSelectedReportCurrentValue = () => {
  if (!cardsData || !selectedReport) return 0;

  switch (selectedReport.key) {
    case "grossSales":
    case "totalSalesOverTime":
      return cardsData.grossSales || 0;

    case "returningRate":
      return cardsData.returningRate || 0;

    case "ordersFulfilled":
      return cardsData.fulfilledOrders || 0;

    case "totalOrders":
      return cardsData.totalOrders || 0;

    case "avgOrder":
      return cardsData.averageOrderValue || 0;

    case "sessionsOverTime":
      return cardsData.sessions || 0;

    case "conversion":
      return cardsData.conversionRate || 0;

    default:
      return 0;
  }
};

const getSelectedReportCompareValue = () => {
  if (!cardsData?.comparison || !selectedReport) return 0;

  switch (selectedReport.key) {
    case "grossSales":
    case "totalSalesOverTime":
      return cardsData.comparison.grossSales || 0;

    case "returningRate":
      return cardsData.comparison.returningRate || 0;

    case "ordersFulfilled":
      return cardsData.comparison.fulfilledOrders || 0;

    case "totalOrders":
      return cardsData.comparison.totalOrders || 0;

    case "avgOrder":
      return cardsData.comparison.averageOrderValue || 0;

    case "sessionsOverTime":
      return cardsData.comparison.sessions || 0;

    case "conversion":
      return cardsData.comparison.conversionRate || 0;

    default:
      return 0;
  }
};

const ReportDelta = () => {
  if (!cardsData?.comparison || !selectedReport) return null;

  const delta = getDelta(
    getSelectedReportCurrentValue(),
    getSelectedReportCompareValue()
  );

  if (delta.hideComparison || delta.percent === null) {
    return <span className="text-gray-400 font-semibold text-[13px]">-</span>;
  }

  if (delta.fromZero || delta.direction === "up") {
    return (
      <span className="inline-flex items-center gap-[4px] font-semibold text-[13px]">
        <svg
          viewBox="0 0 20 20"
          className="w-[15px] h-[15px] text-[#5aa36b]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 16V4" />
          <path d="M5 9l5-5 5 5" />
        </svg>

        <span className="text-gray-700">
          {delta.percent ? `${delta.percent}%` : ""}
        </span>
      </span>
    );
  }

  if (delta.direction === "down") {
    return (
      <span className="inline-flex items-center gap-[4px] font-semibold text-[13px]">
        <svg
          viewBox="0 0 20 20"
          className="w-[15px] h-[15px] text-[#dc6868]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 4v12" />
          <path d="M5 11l5 5 5-5" />
        </svg>

        <span className="text-gray-700">{delta.percent}%</span>
      </span>
    );
  }

  return <span className="text-gray-400 font-semibold text-[13px]">-</span>;
};

const getRowsTotalValue = (rows = [], source = "main") => {
  if (!selectedReport || !cardsData) return "—";

  const dataSource = source === "comparison" ? cardsData.comparison : cardsData;

  if (!dataSource) return "—";

  switch (selectedReport.key) {
    case "totalSalesOverTime":
      return formatMoney(dataSource.grossSales || 0);

    case "grossSales":
      return formatMoney(dataSource.grossSales || 0);

    case "returningRate":
      return formatPercent(dataSource.returningRate || 0);

    case "ordersFulfilled":
      return dataSource.fulfilledOrders || 0;

    case "totalOrders":
      return dataSource.totalOrders || 0;

    case "totalSalesBreakdown":
      return formatMoney(dataSource.salesBreakdown?.totalSales || 0);

    case "salesByChannel":
      return formatMoney(
        (dataSource.salesByChannel || []).reduce(
          (sum, item) => sum + Number(item.totalSales || 0),
          0
        )
      );

    case "avgOrder":
      return formatMoney(dataSource.averageOrderValue || 0);

    case "salesByProduct":
      return formatMoney(
        (dataSource.salesByProduct || []).reduce(
          (sum, item) => sum + Number(item.totalSales || 0),
          0
        )
      );

    case "sessionsOverTime":
      return dataSource.sessions || 0;

    case "conversion":
      return formatPercent(dataSource.conversionRate || 0);

    case "conversionBreakdown":
      return dataSource.conversionBreakdown?.sessions || 0;

    case "topProducts":
      return (dataSource.topSellingProducts || []).reduce(
        (sum, item) => sum + Number(item.unitsSold || 0),
        0
      );

    case "sessionsByDevice":
      return (dataSource.sessionsByDevice || []).reduce(
        (sum, item) => sum + Number(item.sessions || 0),
        0
      );

    case "sessionsByLocation":
      return (dataSource.sessionsByLocation || []).reduce(
        (sum, item) => sum + Number(item.sessions || 0),
        0
      );

    case "salesBySocial":
      return formatMoney(
        (dataSource.salesBySocialReferrer || []).reduce(
          (sum, item) => sum + Number(item.totalSales || 0),
          0
        )
      );

    case "landingPage":
      return (dataSource.sessionsByLandingPage || []).reduce(
        (sum, item) => sum + Number(item.sessions || 0),
        0
      );

    case "sessionsBySocialReferrer":
      return (dataSource.sessionsBySocialReferrer || []).reduce(
        (sum, item) => sum + Number(item.sessions || 0),
        0
      );

    case "salesByReferrer":
      return formatMoney(
        (dataSource.salesByReferrer || []).reduce(
          (sum, item) => sum + Number(item.totalSales || 0),
          0
        )
      );

    case "performanceByReferringChannel":
      return formatMoney(
        (dataSource.performanceByReferringChannel || []).reduce(
          (sum, item) => sum + Number(item.totalSales || 0),
          0
        )
      );

    case "sessionsByReferrer":
      return (dataSource.sessionsByReferrer || []).reduce(
        (sum, item) => sum + Number(item.sessions || 0),
        0
      );

    case "sellThrough":
      return (dataSource.productsBySellThroughRate || []).reduce(
        (sum, item) => sum + Number(item.unitsSold || 0),
        0
      );

    default:
      return rows.length;
  }
};

const getReportTableHeaders = () => {
  if (!selectedReport) {
    return {
      first: TEXT.date,
      second: TEXT.value,
      third: TEXT.details,
    };
  }

  if (selectedReport.key === "returningRate") {
    return {
      first: TEXT.date,
      second: TEXT.customerName,
      third: TEXT.value,
    };
  }

  if (
    selectedReport.key === "ordersFulfilled" ||
    selectedReport.key === "totalOrders"
  ) {
    return {
      first: TEXT.date,
      second: TEXT.orderId,
      third: TEXT.value,
    };
  }

  if (
  selectedReport.key === "topProducts" ||
  selectedReport.key === "salesByProduct" ||
  selectedReport.key === "sellThrough"
) {
  return {
    first: lang === "AR" ? "المنتج" : "Product",
    second: TEXT.value,
    third: TEXT.details,
  };
}

if (selectedReport.key === "cohortAnalysis") {
  return {
    first: lang === "AR" ? "المجموعة" : "Cohort",
    second: lang === "AR" ? "متوسط الاحتفاظ" : "Avg retention",
    third: TEXT.details,
  };
}

  return {
    first: TEXT.date,
    second: TEXT.value,
    third: TEXT.details,
  };
};

const tableHeaders = getReportTableHeaders();
const textAlignClass = lang === "AR" ? "text-right" : "text-left";
const oppositeTextAlignClass = lang === "AR" ? "text-left" : "text-right";

const tableHeadClass = `px-4 py-3 ${textAlignClass} text-[12px] font-semibold text-gray-600`;
const tableCellMainClass = `px-4 py-3 ${textAlignClass} font-medium text-gray-900`;
const tableCellClass = `px-4 py-3 ${textAlignClass} text-gray-700`;
const tableCellMutedClass = `px-4 py-3 ${textAlignClass} text-gray-500`;

const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200/80 ${className}`} />
);

const ReportsTableSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-hidden">
    <div className="p-3 border-b border-gray-100 bg-white">
      <SkeletonBlock className="h-9 w-full max-w-md" />
    </div>

    <div className="p-4 space-y-3">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="grid grid-cols-3 gap-4">
          <SkeletonBlock className="h-5 w-full" />
          <SkeletonBlock className="h-5 w-2/3" />
          <SkeletonBlock className="h-5 w-1/2" />
        </div>
      ))}
    </div>
  </div>
);

const ReportDetailsSkeleton = () => (
  <div className="space-y-4">
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="space-y-2">
          <SkeletonBlock className="h-5 w-56" />
          <SkeletonBlock className="h-4 w-72" />
        </div>

        <SkeletonBlock className="h-6 w-24" />
      </div>

      <SkeletonBlock className="h-[420px] w-full" />
    </div>

    <div className="bg-white border border-gray-200 rounded-xl shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <SkeletonBlock className="h-5 w-40" />
        <SkeletonBlock className="h-4 w-24" />
      </div>

      <div className="p-4 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="grid grid-cols-3 gap-4">
            <SkeletonBlock className="h-5 w-full" />
            <SkeletonBlock className="h-5 w-2/3" />
            <SkeletonBlock className="h-5 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="px-4 py-12 text-center">
    <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
      <ChartSolid className="w-5 h-5 text-gray-400" />
    </div>

    <h3 className="text-[14px] font-semibold text-gray-900">
      {title}
    </h3>

    {description && (
      <p className="text-[13px] text-gray-500 mt-1">
        {description}
      </p>
    )}
  </div>
);

const ErrorState = () => (
  <div className="bg-white border border-red-100 rounded-xl p-8 text-center">
    <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
      <span className="text-red-500 text-lg">!</span>
    </div>

    <h3 className="text-[15px] font-semibold text-gray-900">
      {TEXT.errorTitle}
    </h3>

    <p className="text-[13px] text-gray-500 mt-1">
      {loadError}
    </p>

    <button
      type="button"
      onClick={() => loadCards(mainStart || new Date(), mainEnd || mainStart || new Date(), compareRange)}
      className="mt-4 h-9 px-4 rounded-lg bg-black text-white text-[13px] hover:bg-gray-900 transition"
    >
      {TEXT.retry}
    </button>
  </div>
);

const sanitizeFileName = (value = "report") => {
  return String(value)
    .trim()
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
};

const getReportDateLabel = () => {
  const start = mainStart || new Date();
  const end = mainEnd || start;

  if (start.toDateString() === end.toDateString()) {
    return formatCompareDate(start);
  }

  return `${formatCompareDate(start)} → ${formatCompareDate(end)}`;
};

const getComparisonDateLabel = () => {
  if (!cardsData?.comparison?.from || !cardsData?.comparison?.to) {
    return "";
  }

  const start = parseChartDate(cardsData.comparison.from);
  const end = parseChartDate(cardsData.comparison.to);

  if (start.toDateString() === end.toDateString()) {
    return formatCompareDate(start);
  }

  return `${formatCompareDate(start)} → ${formatCompareDate(end)}`;
};

const getExportFileName = (suffix = "report") => {
  const reportName = selectedReport?.name || TEXT.reports;
  return `${sanitizeFileName(reportName)}-${suffix}`;
};

const buildExportRows = (rows = []) => {
  return rows.map((row) => ({
    [tableHeaders.first]: row.label || "—",
    [tableHeaders.second]: row.value || "—",
    [tableHeaders.third]: row.extra || "—",
  }));
};

const buildTablesExportData = () => {
  const mainRows = buildExportRows(detailRows);
  const comparisonRows = buildExportRows(comparisonDetailRows);

  return {
    mainRows,
    comparisonRows,
  };
};

const downloadCSVFile = (filename, rows = []) => {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);

  const escapeCSV = (value) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => headers.map((header) => escapeCSV(row[header])).join(",")),
  ].join("\n");

  const blob = new Blob([`\uFEFF${csv}`], {
    type: "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();

  URL.revokeObjectURL(link.href);
};

const exportTablesCSV = () => {
  const { mainRows, comparisonRows } = buildTablesExportData();

  const rows = [
    { Section: TEXT.mainTable, Date: getReportDateLabel(), ...{} },
    ...mainRows.map((row) => ({
      Section: TEXT.mainTable,
      Date: getReportDateLabel(),
      ...row,
    })),
    ...(comparisonRows.length
      ? [
          { Section: TEXT.comparisonTable, Date: getComparisonDateLabel(), ...{} },
          ...comparisonRows.map((row) => ({
            Section: TEXT.comparisonTable,
            Date: getComparisonDateLabel(),
            ...row,
          })),
        ]
      : []),
  ];

  downloadCSVFile(getExportFileName("tables"), rows);
};

const exportAllCSV = () => {
  const { mainRows, comparisonRows } = buildTablesExportData();

  const summaryRows = [
    {
      Field: TEXT.reports,
      Value: selectedReport?.name || TEXT.reports,
    },
    {
      Field: TEXT.mainDate,
      Value: getReportDateLabel(),
    },
    {
      Field: TEXT.compareDate,
      Value: getComparisonDateLabel() || TEXT.noComparison,
    },
    {
      Field: TEXT.total,
      Value: getRowsTotalValue(detailRows, "main"),
    },
  ];

  if (cardsData?.comparison) {
    summaryRows.push({
      Field: `${TEXT.comparison} ${TEXT.total}`,
      Value: getRowsTotalValue(comparisonDetailRows, "comparison"),
    });
  }

  const rows = [
    ...summaryRows,
    {},
    { Field: TEXT.mainTable, Value: "" },
    ...mainRows.map((row) => ({
      Field: row[tableHeaders.first],
      Value: row[tableHeaders.second],
      Details: row[tableHeaders.third],
    })),
    ...(comparisonRows.length
      ? [
          {},
          { Field: TEXT.comparisonTable, Value: getComparisonDateLabel() },
          ...comparisonRows.map((row) => ({
            Field: row[tableHeaders.first],
            Value: row[tableHeaders.second],
            Details: row[tableHeaders.third],
          })),
        ]
      : []),
  ];

  downloadCSVFile(getExportFileName("full-page"), rows);
};

const exportTablesExcel = () => {
  const rows = [];

  rows.push([selectedReport?.name || TEXT.reports]);
  rows.push([TEXT.mainDate, getReportDateLabel()]);
  rows.push([TEXT.total, getRowsTotalValue(detailRows, "main")]);
  rows.push([]);

  rows.push([TEXT.mainTable]);
  rows.push([tableHeaders.first, tableHeaders.second, tableHeaders.third]);

  detailRows.forEach((row) => {
    rows.push([
      row.label || "—",
      row.value || "—",
      row.extra || "—",
    ]);
  });

  if (cardsData?.comparison) {
    rows.push([]);
    rows.push([TEXT.comparisonTable]);
    rows.push([TEXT.compareDate, getComparisonDateLabel() || TEXT.noComparison]);
    rows.push([TEXT.total, getRowsTotalValue(comparisonDetailRows, "comparison")]);
    rows.push([tableHeaders.first, tableHeaders.second, tableHeaders.third]);

    if (comparisonDetailRows.length) {
      comparisonDetailRows.forEach((row) => {
        rows.push([
          row.label || "—",
          row.value || "—",
          row.extra || "—",
        ]);
      });
    } else {
      rows.push([TEXT.emptyDetailsTitle, TEXT.emptyDetailsDescription, "—"]);
    }
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  worksheet["!cols"] = [
    { wch: 34 },
    { wch: 28 },
    { wch: 34 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report tables");

  XLSX.writeFile(workbook, `${getExportFileName("tables")}.xlsx`);
};

const exportTablesPDF = () => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  doc.setFontSize(14);
  doc.text(selectedReport?.name || TEXT.reports, 40, 40);

  doc.setFontSize(10);
  doc.text(`${TEXT.mainDate}: ${getReportDateLabel()}`, 40, 58);

  let startY = 82;

  autoTable(doc, {
    startY,
    head: [[tableHeaders.first, tableHeaders.second, tableHeaders.third]],
    body: detailRows.map((row) => [
      row.label || "—",
      row.value || "—",
      row.extra || "—",
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 6,
    },
    headStyles: {
      fillColor: [246, 246, 247],
      textColor: [32, 34, 35],
    },
  });

  if (comparisonDetailRows.length) {
    const nextY = doc.lastAutoTable.finalY + 28;

    doc.setFontSize(10);
    doc.text(`${TEXT.compareDate}: ${getComparisonDateLabel()}`, 40, nextY);

    autoTable(doc, {
      startY: nextY + 16,
      head: [[tableHeaders.first, tableHeaders.second, tableHeaders.third]],
      body: comparisonDetailRows.map((row) => [
        row.label || "—",
        row.value || "—",
        row.extra || "—",
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 6,
      },
      headStyles: {
        fillColor: [246, 246, 247],
        textColor: [32, 34, 35],
      },
    });
  }

  doc.save(`${getExportFileName("tables")}.pdf`);
};

const getChartExportImage = () => {
  const chartCanvas = document.querySelector("#reports-chart-export canvas");

  if (!chartCanvas) return "";

  try {
    return chartCanvas.toDataURL("image/png", 1);
  } catch (err) {
    console.error("Failed to export chart image", err);
    return "";
  }
};

const exportAllPDF = () => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const chartImage = getChartExportImage();

  doc.setFontSize(16);
  doc.text(selectedReport?.name || TEXT.reports, 40, 42);

  doc.setFontSize(10);
  doc.text(`${TEXT.mainDate}: ${getReportDateLabel()}`, 40, 62);

  if (cardsData?.comparison) {
    doc.text(`${TEXT.compareDate}: ${getComparisonDateLabel()}`, 40, 78);
  }

  doc.text(`${TEXT.total}: ${getRowsTotalValue(detailRows, "main")}`, 40, 96);

  if (cardsData?.comparison) {
    doc.text(
      `${TEXT.comparison} ${TEXT.total}: ${getRowsTotalValue(
        comparisonDetailRows,
        "comparison"
      )}`,
      260,
      96
    );
  }

  let startY = 120;

  if (chartImage) {
    doc.addImage(chartImage, "PNG", 40, startY, 760, 250);
    startY += 275;
  }

  autoTable(doc, {
    startY,
    head: [[TEXT.details, TEXT.value]],
    body: [
      [TEXT.category, selectedReport?.category || "—"],
      [TEXT.currencyRateTitle, currency === "USD" ? "USD" : `LBP ${exchangeRate}`],
      [TEXT.mainDate, getReportDateLabel()],
      [TEXT.compareDate, getComparisonDateLabel() || TEXT.noComparison],
    ],
    styles: {
      fontSize: 8,
      cellPadding: 6,
    },
    headStyles: {
      fillColor: [246, 246, 247],
      textColor: [32, 34, 35],
    },
  });

  startY = doc.lastAutoTable.finalY + 24;

  autoTable(doc, {
    startY,
    head: [[tableHeaders.first, tableHeaders.second, tableHeaders.third]],
    body: detailRows.map((row) => [
      row.label || "—",
      row.value || "—",
      row.extra || "—",
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 6,
    },
    headStyles: {
      fillColor: [246, 246, 247],
      textColor: [32, 34, 35],
    },
  });

  if (cardsData?.comparison) {
    const nextY = doc.lastAutoTable.finalY + 24;

    autoTable(doc, {
      startY: nextY,
      head: [[tableHeaders.first, tableHeaders.second, tableHeaders.third]],
      body: comparisonDetailRows.length
        ? comparisonDetailRows.map((row) => [
            row.label || "—",
            row.value || "—",
            row.extra || "—",
          ])
        : [[TEXT.emptyDetailsTitle, TEXT.emptyDetailsDescription, "—"]],
      styles: {
        fontSize: 8,
        cellPadding: 6,
      },
      headStyles: {
        fillColor: [246, 246, 247],
        textColor: [32, 34, 35],
      },
    });
  }

  doc.save(`${getExportFileName("full-page")}.pdf`);
};

const printHtml = ({ includeChart = true }) => {
  const chartCanvas = document.querySelector("#reports-chart-export canvas");
  const chartImage = chartCanvas ? chartCanvas.toDataURL("image/png") : "";

  const mainRowsHtml = detailRows
    .map(
      (row) => `
        <tr>
          <td>${row.label || "—"}</td>
          <td>${row.value || "—"}</td>
          <td>${row.extra || "—"}</td>
        </tr>
      `
    )
    .join("");

  const comparisonRowsHtml = comparisonDetailRows
    .map(
      (row) => `
        <tr>
          <td>${row.label || "—"}</td>
          <td>${row.value || "—"}</td>
          <td>${row.extra || "—"}</td>
        </tr>
      `
    )
    .join("");

  const comparisonHtml = comparisonDetailRows.length
    ? `
      <section>
        <div class="section-title">
          <span>${TEXT.comparisonTable}</span>
          <span>${TEXT.total}: ${getRowsTotalValue(comparisonDetailRows, "comparison")}</span>
        </div>
        <div class="date-label">${getComparisonDateLabel()}</div>
        <table>
          <thead>
            <tr>
              <th>${tableHeaders.first}</th>
              <th>${tableHeaders.second}</th>
              <th>${tableHeaders.third}</th>
            </tr>
          </thead>
          <tbody>${comparisonRowsHtml}</tbody>
        </table>
      </section>
    `
    : "";

  const win = window.open("", "", "width=1100,height=760");

  win.document.write(`
    <html dir="${lang === "AR" ? "rtl" : "ltr"}">
      <head>
        <title>${selectedReport?.name || TEXT.reports}</title>
        <style>
          body {
            font-family: Inter, Arial, sans-serif;
            background: #ffffff;
            color: #202223;
            padding: 28px;
          }

          .header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 16px;
            margin-bottom: 18px;
          }

          h1 {
            font-size: 22px;
            margin: 0 0 6px;
          }

          .meta {
            font-size: 12px;
            color: #6b7280;
            line-height: 1.8;
          }

          .total {
            font-size: 13px;
            color: #374151;
            text-align: ${lang === "AR" ? "left" : "right"};
          }

          .chart {
            margin: 18px 0;
            padding: 14px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
          }

          .chart img {
            width: 100%;
            max-height: 360px;
            object-fit: contain;
          }

          section {
            margin-top: 18px;
          }

          .section-title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .date-label {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 8px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }

          th, td {
            border: 1px solid #e5e7eb;
            padding: 8px;
            text-align: ${lang === "AR" ? "right" : "left"};
          }

          th {
            background: #f6f6f7;
            color: #374151;
            font-weight: 600;
          }

          @media print {
            body {
              padding: 18px;
            }

            .chart {
              page-break-inside: avoid;
            }

            table {
              page-break-inside: auto;
            }

            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
          }
        </style>
      </head>

      <body>
        <div class="header">
          <div>
            <h1>${selectedReport?.name || TEXT.reports}</h1>
            <div class="meta">
              <div>${TEXT.mainDate}: ${getReportDateLabel()}</div>
              <div>${TEXT.compareDate}: ${getComparisonDateLabel() || TEXT.noComparison}</div>
              <div>${TEXT.category}: ${selectedReport?.category || "—"}</div>
            </div>
          </div>

          <div class="total">
            <div>${TEXT.total}: <strong>${getRowsTotalValue(detailRows, "main")}</strong></div>
            ${
              cardsData?.comparison
                ? `<div>${TEXT.comparison}: <strong>${getRowsTotalValue(
                    comparisonDetailRows,
                    "comparison"
                  )}</strong></div>`
                : ""
            }
          </div>
        </div>

        ${
          includeChart && chartImage
            ? `<div class="chart"><img src="${chartImage}" /></div>`
            : ""
        }

        <section>
          <div class="section-title">
            <span>${TEXT.mainTable}</span>
            <span>${TEXT.total}: ${getRowsTotalValue(detailRows, "main")}</span>
          </div>
          <div class="date-label">${getReportDateLabel()}</div>
          <table>
            <thead>
              <tr>
                <th>${tableHeaders.first}</th>
                <th>${tableHeaders.second}</th>
                <th>${tableHeaders.third}</th>
              </tr>
            </thead>
            <tbody>${mainRowsHtml}</tbody>
          </table>
        </section>

        ${comparisonHtml}
      </body>
    </html>
  `);

  win.document.close();
  win.focus();

  setTimeout(() => {
    win.print();
    win.close();
  }, 350);
};

const handleExportFormat = (format) => {
  if (!selectedReport) return;

  if (exportMode === "all") {
    if (format === "print") {
      printHtml({ includeChart: true });
    }


    if (format === "pdf") {
      exportAllPDF();
    }
  }

  if (exportMode === "tables") {
    if (format === "print") {
      printHtml({ includeChart: false });
    }

    if (format === "excel") {
      exportTablesExcel();
    }

    if (format === "pdf") {
      exportTablesPDF();
    }
  }

  setShowExportModal(false);
  setExportMode(null);
};

const goBackToAnalytics = () => {
  const start = mainStart || new Date();
  const end = mainEnd || start;

  navigate("/admin/analytics", {
    state: {
      fromReports: true,
      mainStart: formatLocalDate(start),
      mainEnd: formatLocalDate(end),
      compareStart: compareRange?.start ? formatLocalDate(compareRange.start) : null,
      compareEnd: compareRange?.end ? formatLocalDate(compareRange.end) : null,
      compareType,
      currency,
      exchangeRate,
    },
  });
};

  return (
    <div
      className={`flex bg-[#f1f1f1] min-h-screen w-full max-w-full overflow-x-hidden ${
        isOpen ? "overflow-hidden" : ""
      }`}
    >
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div
        className={`
          flex-1 min-w-0 w-full max-w-full overflow-x-hidden lg:overflow-visible
          ${lang === "AR" ? "" : "lg:ml-64"}
          transition-all duration-300
        `}
      >
        <AdminHeader
  setIsOpen={setIsOpen}
  beforeSearch={
    cameFromAnalytics ? (
      <button
        type="button"
        onClick={goBackToAnalytics}
        className="
          hidden sm:inline-flex
          h-[34px]
          items-center
          gap-1.5
          rounded-md
          border border-[#3a3a3a]
          bg-[#2c2c2c]
          px-3
          text-[13px]
          font-medium
          text-gray-100
          shadow-[0_1px_0_rgba(255,255,255,0.05)]
          hover:bg-[#363636]
          active:bg-[#242424]
          transition
          whitespace-nowrap
        "
      >
        <FiChevronLeft
          className={`
            text-[16px]
            ${lang === "AR" ? "rotate-180" : ""}
          `}
        />
        <span>{TEXT.analytics}</span>
      </button>
    ) : null
  }
/>

        <main className="w-full max-w-full min-w-0 p-2 sm:p-4 lg:p-6 overflow-x-hidden lg:overflow-visible">
  <div className="lg:hidden min-h-[calc(100vh-90px)] flex items-center justify-center px-4">
    <div className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-sm p-6 text-center">
      <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <ChartSolid className="w-5 h-5 text-gray-700" />
      </div>

      <h2 className="text-[16px] font-semibold text-gray-900">
        {lang === "AR" ? "التقارير غير متاحة على الهاتف" : "Reports are not available on mobile"}
      </h2>

      <p className="text-[13px] text-gray-500 mt-2 leading-6">
        {lang === "AR"
          ? "يرجى فتح هذه الصفحة من شاشة كمبيوتر أو جهاز لوحي كبير."
          : "Please open this page from a desktop or large tablet screen."}
      </p>
    </div>
  </div>

  <div className="hidden lg:block">
    {!canAccess ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-semibold">
                {lang === "AR"
                  ? "غير مصرح لك بعرض التقارير"
                  : "You are not allowed to view reports"}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-row items-center justify-between gap-3 mb-5 w-full">
  <div className="mx-1 flex items-center gap-2 min-w-0">
    <button
      type="button"
      onClick={() => {
        if (selectedReport) {
          setSelectedReport(null);
        }
      }}
      className={`
        w-8 h-8 flex items-center justify-center rounded-md transition
        ${selectedReport ? "hover:bg-gray-200 cursor-pointer" : "cursor-default"}
      `}
      aria-label={TEXT.title}
    >
      <DocumentTextIcon className="w-[21px] h-[21px] text-gray-900" />
    </button>

    {selectedReport && (
      <FiChevronRight
        className={`
          text-gray-400 shrink-0
          ${lang === "AR" ? "rotate-180" : ""}
        `}
      />
    )}

    <h1 className="text-[20px] font-semibold text-gray-900 truncate">
      {selectedReport ? selectedReport.name : TEXT.title}
    </h1>
  </div>

  {selectedReport && (
    <button
      type="button"
      onClick={() => setShowExportModal(true)}
      className="
        h-8 px-3
        bg-[#202223]
        text-white
        border border-[#202223]
        rounded-lg
        text-[12px] font-medium
        shadow-[0_1px_0_rgba(0,0,0,0.08),0_2px_5px_rgba(0,0,0,0.10)]
        hover:bg-black
        transition
        whitespace-nowrap
      "
    >
      {TEXT.export}
    </button>
  )}
</div>

              <div className="flex flex-row sm:flex-wrap items-center gap-1.5 sm:gap-3 mb-6 w-full max-w-full min-w-0">
                <button
  type="button"
  onClick={() => {
    setShowCompareCalendar(true);
    setDateView("main");
    setDateTab("custom");

    if (mainRange) {
      setMainStart(mainRange.start);
      setMainEnd(mainRange.end);
      setSelectedDate(mainRange.end);
      setCalendarBaseDate(mainRange.end);
    } else {
      const today = new Date();
      setMainStart(today);
      setMainEnd(today);
      setSelectedDate(today);
      setCalendarBaseDate(today);
    }
  }}
  className="
    flex-1 sm:flex-none min-w-0
    flex items-center justify-between gap-1 sm:gap-2
    h-8 px-2 sm:px-3 w-full sm:w-auto max-w-full
    bg-white
    border border-gray-200
    rounded-lg
    shadow-[0_1px_0_rgba(0,0,0,0.04)]
    text-[11px] sm:text-[12px] font-medium text-gray-900
    hover:bg-gray-50
    transition
  "
>
  <FaCalendarAlt className="text-gray-900 text-sm" />

  <span className="truncate min-w-0">{getMainButtonLabel()}</span>

  {mainRange && (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        resetToToday();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          resetToToday();
        }
      }}
      className="ml-1 text-gray-400 hover:text-black text-sm cursor-pointer"
    >
      ✕
    </span>
  )}

  <FiChevronDown className="text-gray-900 text-sm" />
</button>

                <div className="relative compare-dropdown flex-1 sm:flex-none min-w-0 sm:w-auto">
  <button
    type="button"
    onClick={() => setShowCompareDropdown((p) => !p)}
    className="flex items-center justify-between gap-1 sm:gap-2 h-8 px-2 sm:px-3 w-full sm:w-auto max-w-full bg-white border border-gray-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04)] text-[11px] sm:text-[12px] font-medium text-gray-900 hover:bg-gray-50 transition"
  >
    <FaCalendarAlt className="text-gray-900 text-sm" />

    <span className="truncate min-w-0">{getCompareButtonLabel()}</span>

    {compareRange && (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();

          const start = mainStart || new Date();
          const end = mainEnd || start;

          setCompareRange(null);
          setCompareType("none");
          loadCards(start, end, null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();

            const start = mainStart || new Date();
            const end = mainEnd || start;

            setCompareRange(null);
            setCompareType("none");
            loadCards(start, end, null);
          }
        }}
        className="ml-1 text-gray-400 hover:text-black text-sm cursor-pointer"
      >
        ✕
      </span>
    )}

    <FiChevronDown className="text-gray-900 text-sm" />
  </button>

  {showCompareDropdown && (
    <div
      className="
        fixed sm:absolute
        left-3 right-3 sm:left-0 sm:right-auto
        top-[124px] sm:top-auto
        mt-0 sm:mt-2
        w-auto sm:w-[260px]
        bg-white border border-gray-200
        rounded-xl shadow-lg
        py-1 z-[999]
        overflow-hidden
      "
    >
      {[
        { key: "none", label: TEXT.compareNone },
        { key: "yesterday", label: TEXT.compareYesterday },
        { key: "prevYear", label: TEXT.comparePrevYear },
        { key: "prevYearMatch", label: TEXT.comparePrevYearMatch },
        { key: "custom", label: TEXT.compareCustom },
      ].map((item) => (
        <button
          type="button"
          key={item.key}
          onClick={() => {
            if (item.key === "none") {
              const start = mainStart || new Date();
              const end = mainEnd || start;

              setCompareRange(null);
              setCompareType("none");
              setShowCompareDropdown(false);
              loadCards(start, end, null);
              return;
            }

            setCompareType(item.key);
            setShowCompareDropdown(false);

            if (item.key === "custom") {
              setShowCustomCompareModal(true);

              const today = new Date();

              setCompareStartDate(null);
              setCompareEndDate(null);
              setSelectedDate(today);
              setCalendarBaseDate(today);
              setCalendarOffset(0);

              return;
            }

            const start = mainStart || new Date();
            const end = mainEnd || start;
            const nextCompare = buildCompareRange(item.key, start, end);

            setCompareRange(nextCompare);
            loadCards(start, end, nextCompare);
          }}
          className={`
            w-full
            ${lang === "AR" ? "text-right" : "text-left"}
            px-4 py-2.5
            text-[11px] sm:text-[13px]
            leading-none
            whitespace-nowrap
            overflow-hidden
            hover:bg-gray-100 transition
            ${compareType === item.key ? "bg-gray-100 font-medium" : ""}
          `}
        >
          <span className="block truncate">{item.label}</span>
        </button>
      ))}
    </div>
  )}
</div>

                <button
                  type="button"
                  onClick={() => {
                    if (currency === "USD") {
                      setShowCurrencyModal(true);
                    } else {
                      setCurrency("USD");
                    }
                  }}
                  className="flex-1 sm:flex-none min-w-0 flex items-center justify-center sm:justify-start gap-1 sm:gap-2 h-8 px-2 sm:px-3 w-full sm:w-auto bg-white border border-gray-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04)] text-[11px] sm:text-[12px] font-medium text-gray-900 hover:bg-gray-50 transition"
                >
                  <FaExchangeAlt className="text-gray-900 text-sm" />
                  <span className="truncate min-w-0">
                    {currency === "USD"
                      ? "USD"
                      : `LBP ${exchangeRate.toLocaleString()} ل.ل`}
                  </span>
                </button>
              </div>

              {loadError ? (
  <ErrorState />
) : !selectedReport ? (
  loading && !cardsData ? (
    <ReportsTableSkeleton />
  ) : (
    <div className="bg-white border border-gray-200 rounded-xl shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-hidden">
  <div className="p-3 border-b border-gray-100 bg-white">
    <div className="relative w-full sm:max-w-md">
      <FaSearch
        className={`
          absolute top-1/2 -translate-y-1/2 text-gray-400 text-[13px]
          ${lang === "AR" ? "right-3" : "left-3"}
        `}
      />

     <input
  value={reportSearch}
  onChange={(e) => setReportSearch(e.target.value)}
  placeholder={TEXT.searchReports}
  className={`
    w-full h-9 rounded-lg border border-gray-200 bg-white
    text-[13px] text-gray-900
    focus:outline-none focus:ring-2 focus:ring-black/10
    ${
      lang === "AR"
        ? "pr-9 pl-9 text-right"
        : "pl-9 pr-9 text-left"
    }
  `}
/>

{reportSearch && (
  <button
    type="button"
    onClick={() => setReportSearch("")}
    className={`
      absolute top-1/2 -translate-y-1/2
      w-6 h-6 rounded-md
      flex items-center justify-center
      text-gray-400 hover:text-black hover:bg-gray-100
      transition
      ${lang === "AR" ? "left-2" : "right-2"}
    `}
  >
    ✕
  </button>
)}
    </div>
  </div>

  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className={tableHeadClass}>
                            {TEXT.name}
                          </th>
                          <th className={tableHeadClass}>
                            {TEXT.category}
                          </th>
                          <th className={tableHeadClass}>
                            {TEXT.createdBy}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredReportRows.map((row) => (
                          <tr
                            key={row.key}
                            onClick={() => setSelectedReport(row)}
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition"
                          >
                            <td className={tableCellMainClass}>
                              {row.name}
                            </td>
                            <td className={`px-4 py-3 ${textAlignClass} text-gray-600 capitalize`}>
                              {row.category}
                            </td>
                            <td className={`px-4 py-3 ${textAlignClass} text-gray-600`}>
                              {TEXT.admin}
                            </td>
                          </tr>
                        ))}
                        {filteredReportRows.length === 0 && (
  <tr>
    <td colSpan={3}>
      <EmptyState
        title={TEXT.emptyReportsTitle}
        description={TEXT.emptyReportsDescription}
      />
    </td>
  </tr>
)}
                      </tbody>
                    </table>
                  </div>
                                </div>
              )
            ) : loading && !cardsData ? (
  <ReportDetailsSkeleton />
) : (
  <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2">
  <h2 className="text-[16px] font-semibold text-gray-900">
    {selectedReport.name}
  </h2>

  <ReportDelta />
</div>

                        <p className="text-[12px] text-gray-500 mt-1">
                          {TEXT.mainDate}: {getMainButtonLabel()}
                          {compareRange ? ` • ${TEXT.compareDate}: ${getCompareButtonLabel()}` : ""}
                        </p>
                      </div>

                      <span className="px-2 py-1 rounded-md bg-gray-100 text-[11px] font-medium text-gray-700 capitalize">
                        {selectedReport.category}
                      </span>
                    </div>

                    {loading ? (
  <SkeletonBlock className="h-[360px] md:h-[460px] w-full" />
) : chartData ? (
                      <div id="reports-chart-export" className="h-[360px] md:h-[460px]">
                        {chartData?.type === "bar" ? (
  <Bar
    data={chartData}
    options={{
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: Boolean(cardsData?.comparison),
          position: "bottom",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            color: "#6B7280",
            font: {
              size: 11,
            },
          },
        },
        tooltip: {
          backgroundColor: "#ffffff",
          titleColor: "#111827",
          bodyColor: "#374151",
          borderColor: "#E5E7EB",
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (items) => {
              const index = items?.[0]?.dataIndex || 0;
              return chartData.originalLabels?.[index] || chartData.labels?.[index] || "";
            },
            label: (ctx) => {
              const formatter = getChartValueFormatter(chartData.valueType);
              return `${ctx.dataset.label}: ${formatter(ctx.raw || 0)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#6B7280",
            font: {
              size: 11,
            },
            maxRotation: 0,
            autoSkip: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(229,231,235,0.8)",
          },
          border: {
            display: false,
          },
          ticks: {
            color: "#6B7280",
            font: {
              size: 11,
            },
            callback: (value) => {
              if (chartData.valueType === "money") {
                return currency === "LBP"
                  ? `${Math.round(Number(value || 0) * exchangeRate).toLocaleString()}`
                  : `$${Number(value || 0).toLocaleString()}`;
              }

              if (chartData.valueType === "percent") {
                return `${value}%`;
              }

              return Number(value || 0).toLocaleString();
            },
          },
          title: {
            display: Boolean(getChartYAxisLabel(chartData.valueType)),
            text: getChartYAxisLabel(chartData.valueType),
            color: "#6B7280",
            font: {
              size: 11,
            },
          },
        },
      },
    }}
  />
) : (
  <Line
    data={chartData}
    options={{
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: Boolean(cardsData?.comparison),
          position: "bottom",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            color: "#6B7280",
            font: {
              size: 11,
            },
          },
        },
        tooltip: {
          backgroundColor: "#ffffff",
          titleColor: "#111827",
          bodyColor: "#374151",
          borderColor: "#E5E7EB",
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (items) => {
              const index = items?.[0]?.dataIndex || 0;
              const isCompare = items?.[0]?.datasetIndex === 1;

              const point = isCompare
                ? chartData._compareTrend?.[index]
                : chartData._mainTrend?.[index];

              return formatReportTooltipTitle(point);
            },
            label: (ctx) => {
              const formatter = getChartValueFormatter(chartData.valueType);
              return `${ctx.dataset.label}: ${formatter(ctx.raw || 0)}`;
            },
          },
        },
      },
      elements: {
        line: {
          borderJoinStyle: "round",
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#6B7280",
            font: {
              size: 11,
            },
            maxRotation: 0,
            autoSkip: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(229,231,235,0.8)",
          },
          border: {
            display: false,
          },
          ticks: {
            color: "#6B7280",
            font: {
              size: 11,
            },
            callback: (value) => {
              const formatter = getChartValueFormatter(chartData.valueType);
              return formatter(value);
            },
          },
        },
      },
    }}
  />
)}
                      </div>
                    ) : (
                      <div className="h-[260px] flex items-center justify-center">
  <EmptyState
    title={TEXT.emptyDetailsTitle}
    description={TEXT.emptyDetailsDescription}
  />
</div>
                    )}
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
  <h3 className="text-[14px] font-semibold text-gray-900">
  {(mainStart || new Date()).toDateString() ===
  (mainEnd || mainStart || new Date()).toDateString()
    ? formatCompareDate(mainStart || new Date())
    : `${formatCompareDate(mainStart || new Date())} → ${formatCompareDate(
        mainEnd || mainStart || new Date()
      )}`}
</h3>

  <div className="text-[12px] text-gray-500">
    {TEXT.total}:{" "}
    <span className="font-semibold text-gray-900">
      {getRowsTotalValue(detailRows, "main")}
    </span>
  </div>
</div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className={tableHeadClass}>
                                {tableHeaders.first}
                            </th>
                            <th className={tableHeadClass}>
                              {tableHeaders.second}
                            </th>
                            <th className={tableHeadClass}>
                              {tableHeaders.third}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {detailRows.length ? (
                            detailRows.map((row, index) => (
                              <tr key={`${row.label}-${index}`} className="border-b border-gray-100">
                                <td className={tableCellMainClass}>
                                  {row.label}
                                </td>
                                <td className={tableCellClass}>
                                  {row.value}
                                </td>
                                <td className={tableCellMutedClass}>
                                  {row.extra || "—"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
  <td colSpan={3}>
    <EmptyState
      title={TEXT.emptyDetailsTitle}
      description={TEXT.emptyDetailsDescription}
    />
  </td>
</tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {cardsData?.comparison && comparisonDetailRows.length > 0 && (
  <div className="bg-white border border-gray-200 rounded-xl shadow-[0_1px_0_rgba(0,0,0,0.04)] overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
  <div>
    <h3 className="text-[14px] font-semibold text-gray-900">
      {TEXT.comparisonPeriod}
    </h3>

    {cardsData.comparison.from && cardsData.comparison.to && (
  <p className="text-[12px] text-gray-500 mt-1">
    {parseChartDate(cardsData.comparison.from).toDateString() ===
    parseChartDate(cardsData.comparison.to).toDateString()
      ? formatCompareDate(parseChartDate(cardsData.comparison.from))
      : `${formatCompareDate(parseChartDate(cardsData.comparison.from))} → ${formatCompareDate(
          parseChartDate(cardsData.comparison.to)
        )}`}
  </p>
)}
  </div>

  <div className="text-[12px] text-gray-500 whitespace-nowrap pt-[2px]">
    {TEXT.total}:{" "}
    <span className="font-semibold text-gray-900">
      {getRowsTotalValue(comparisonDetailRows, "comparison")}
    </span>
  </div>
</div>

    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className={tableHeadClass}>
              {tableHeaders.first}
            </th>
            <th className={tableHeadClass}>
              {tableHeaders.second}
            </th>
            <th className={tableHeadClass}>
              {tableHeaders.third}
            </th>
          </tr>
        </thead>

        <tbody>
          {comparisonDetailRows.map((row, index) => (
            <tr
              key={`comparison-${row.label}-${index}`}
              className="border-b border-gray-100"
            >
              <td className={tableCellMainClass}>
                {row.label}
              </td>
              <td className={tableCellClass}>
                {row.value}
              </td>
              <td className={tableCellMutedClass}>
                {row.extra || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}
                </div>
              )}
            </>
                    )}
  </div>
</main>
      </div>

{showCompareCalendar && (
  <div
    onMouseDown={() => setShowCompareCalendar(false)}
    className="fixed inset-0 z-[9999] bg-black/30 flex items-start justify-center pt-[90px] px-3"
  >
    <div
      ref={modalRef}
      onMouseDown={(e) => e.stopPropagation()}
      className="w-full max-w-[620px] bg-[#F1F1F1] border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] min-h-[310px]">
        <div className="bg-[#EBEBEB] border-b sm:border-b-0 sm:border-r border-gray-200 p-3 space-y-1">
          {dateView !== "main" && (
            <button
              type="button"
              onClick={() => setDateView("main")}
              className="w-full text-left px-3 py-1.5 text-[12px] rounded-md hover:bg-[#E7E7E7]"
            >
              ← {lang === "AR" ? "رجوع" : "Back"}
            </button>
          )}

          {dateView === "main" && (
            <>
              {[
                { key: "today", label: TEXT.todayLabel },
                { key: "yesterday", label: TEXT.yesterdayLabel },
                { key: "last", label: TEXT.lastLabel, next: true },
                { key: "quarters", label: TEXT.quartersLabel, next: true },
                { key: "custom", label: TEXT.customRangeLabel },
              ].map((item) => (
                <button
                  type="button"
                  key={item.key}
                  onClick={() => {
                    if (item.key === "last") {
                      setDateView("last");
                      return;
                    }

                    if (item.key === "quarters") {
                      setDateView("quarters");
                      return;
                    }

                    setActiveQuickRange(item.key);

                    if (item.key === "today") {
                      const today = new Date();
                      setRangeStart(today);
                      setRangeEnd(today);
                      applyRange(today, today);
                    }

                    if (item.key === "yesterday") {
                      const d = new Date();
                      d.setDate(d.getDate() - 1);
                      setRangeStart(d);
                      setRangeEnd(d);
                      applyRange(d, d);
                    }

                    if (item.key === "custom") {
                      setMainStart(null);
                      setMainEnd(null);
                      setRangeStart(null);
                      setRangeEnd(null);
                      setSelectedDate(null);
                    }
                  }}
                  className={`
                    group w-full flex justify-between items-center
                    px-3 py-1.5 text-[13px]
                    rounded-md
                    transition-all duration-150
                    whitespace-nowrap
                    ${
                      (item.key === "today" && activeQuickRange === "today") ||
                      (item.key === "yesterday" && activeQuickRange === "yesterday") ||
                      (item.key === "custom" && activeQuickRange === "custom") ||
                      (item.key === "last" && activeQuickRange === "last") ||
                      (item.key === "quarters" && activeQuickRange === "quarter")
                        ? "bg-white border border-gray-300 shadow-sm font-medium"
                        : "hover:bg-[#E7E7E7]"
                    }
                  `}
                >
                  {item.label}

                  {item.next && (
                    <span className="opacity-0 group-hover:opacity-100 transition">
                      ›
                    </span>
                  )}
                </button>
              ))}
            </>
          )}

          {dateView === "last" && (
            <>
              {[
                { label: TEXT.last24h, days: 1 },
                { label: TEXT.last7d, days: 7 },
                { label: TEXT.last30d, days: 30 },
                { label: TEXT.last90d, days: 90 },
                { label: TEXT.lastYear, days: 365 },
              ].map((item, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(end.getDate() - item.days + 1);

                    setRangeStart(start);
                    setRangeEnd(end);
                    applyRange(start, end);
                    setActiveQuickRange("last");
                  }}
                  className="w-full text-left px-3 py-1.5 text-[12px] rounded-md hover:bg-[#E7E7E7] active:bg-[#DCDCDC] transition"
                >
                  {item.label}
                </button>
              ))}
            </>
          )}

          {dateView === "quarters" && (
            <>
              {getLastQuarters().map((q, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => {
                    setRangeStart(q.start);
                    setRangeEnd(q.end);
                    applyRange(q.start, q.end);
                    setActiveQuickRange("quarter");
                  }}
                  className="w-full text-left px-3 py-1.5 text-[12px] rounded-md hover:bg-[#E7E7E7] active:bg-[#DCDCDC] transition"
                >
                  {q.label}
                </button>
              ))}
            </>
          )}
        </div>

        <div className="bg-white p-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <input
              readOnly
              value={rangeStart ? formatCompareDate(rangeStart) : ""}
              placeholder={lang === "AR" ? "من" : "From"}
              className="w-full border border-gray-300 px-2 py-1.5 text-[13px] rounded-md bg-gray-100 text-center"
            />

            <span className="text-gray-400 text-sm font-medium">VS</span>

            <input
              readOnly
              value={rangeEnd ? formatCompareDate(rangeEnd) : ""}
              placeholder={lang === "AR" ? "إلى" : "To"}
              className="w-full border border-gray-300 px-2 py-1.5 text-[13px] rounded-md bg-gray-100 text-center"
            />
          </div>

          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCalendarOffset((p) => p - 1)}
              className="w-8 h-8 rounded-md hover:bg-gray-100"
            >
              ‹
            </button>

            <p className="text-[13px] font-semibold text-gray-900">
              {getVisibleCalendarMonthLabel()}
            </p>

            <button
              type="button"
              onClick={() => setCalendarOffset((p) => p + 1)}
              className="w-8 h-8 rounded-md hover:bg-gray-100"
            >
              ›
            </button>
          </div>

          <div className="flex justify-center">
            <Calendar
              monthOffset={calendarOffset}
              selectedDate={selectedDate}
              calendarBaseDate={calendarBaseDate}
              setSelectedDate={setSelectedDate}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              setRangeStart={setRangeStart}
              setRangeEnd={setRangeEnd}
              setCalendarOffset={setCalendarOffset}
              activeQuickRange={activeQuickRange}
              setActiveQuickRange={setActiveQuickRange}
            />
          </div>

          <div className="flex justify-between items-center gap-2 mt-5 pt-4 border-t border-gray-100">
            <p className="text-[12px] text-gray-500 truncate">
              {TEXT.selectedRange}:{" "}
              <span className="font-medium text-gray-700">
                {getDraftRangeLabel(rangeStart, rangeEnd) || "—"}
              </span>
            </p>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCompareCalendar(false)}
                className="h-9 px-3 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
              >
                {TEXT.cancel}
              </button>

              <button
                type="button"
                onClick={() => {
                  const start = rangeStart || mainStart || new Date();
                  const end = rangeEnd || mainEnd || start;

                  applyRange(start, end);
                  setShowCompareCalendar(false);
                }}
                className="h-9 px-3 rounded-lg bg-black text-white text-sm hover:bg-gray-900"
              >
                {TEXT.applyBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

{showExportModal && (
  <div
    onMouseDown={() => {
      setShowExportModal(false);
      setExportMode(null);
    }}
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4"
  >
    <div
      onMouseDown={(e) => e.stopPropagation()}
      className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="px-5 py-4 border-b border-gray-100 bg-[#f6f6f7]">
        <h3 className="text-[15px] font-semibold text-gray-900">
          {exportMode ? TEXT.chooseExportFormat : TEXT.exportReport}
        </h3>

        <p className="text-[12px] text-gray-500 mt-1">
          {!exportMode
            ? TEXT.exportLaterNote
            : exportMode === "all"
            ? TEXT.exportAllPageDescription
            : TEXT.exportTablesDescription}
        </p>
      </div>

      {!exportMode ? (
        <div className="py-1">
          <button
            type="button"
            onClick={() => setExportMode("all")}
            className={`
              w-full px-5 py-3 text-[13px] text-gray-900 hover:bg-gray-100 transition
              ${lang === "AR" ? "text-right" : "text-left"}
            `}
          >
            {TEXT.exportAllPage}
          </button>

          <button
            type="button"
            onClick={() => setExportMode("tables")}
            className={`
              w-full px-5 py-3 text-[13px] text-gray-900 hover:bg-gray-100 transition
              ${lang === "AR" ? "text-right" : "text-left"}
            `}
          >
            {TEXT.exportWithoutChart}
          </button>
        </div>
      ) : (
        <div className="py-1">
          <button
            type="button"
            onClick={() => handleExportFormat("print")}
            className={`
              w-full px-5 py-3 text-[13px] text-gray-900 hover:bg-gray-100 transition
              ${lang === "AR" ? "text-right" : "text-left"}
            `}
          >
            {TEXT.print}
          </button>

          {exportMode === "tables" && (
            <button
              type="button"
              onClick={() => handleExportFormat("excel")}
              className={`
                w-full px-5 py-3 text-[13px] text-gray-900 hover:bg-gray-100 transition
                ${lang === "AR" ? "text-right" : "text-left"}
              `}
            >
              {TEXT.excel}
            </button>
          )}

          <button
            type="button"
            onClick={() => handleExportFormat("pdf")}
            className={`
              w-full px-5 py-3 text-[13px] text-gray-900 hover:bg-gray-100 transition
              ${lang === "AR" ? "text-right" : "text-left"}
            `}
          >
            {TEXT.pdf}
          </button>
        </div>
      )}

      <div className="px-5 py-4 border-t border-gray-100 bg-[#f6f6f7] flex justify-between gap-2">
        {exportMode ? (
          <button
            type="button"
            onClick={() => setExportMode(null)}
            className="h-9 px-4 rounded-lg border border-gray-300 bg-white text-[13px] hover:bg-gray-50"
          >
            {TEXT.backToExportOptions}
          </button>
        ) : (
          <span />
        )}

        <button
          type="button"
          onClick={() => {
            setShowExportModal(false);
            setExportMode(null);
          }}
          className="h-9 px-4 rounded-lg border border-gray-300 bg-white text-[13px] hover:bg-gray-50"
        >
          {TEXT.cancel}
        </button>
      </div>
    </div>
  </div>
)}

{showCustomCompareModal && (
  <div
    onMouseDown={() => setShowCustomCompareModal(false)}
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] px-3"
  >
    <div
      onMouseDown={(e) => e.stopPropagation()}
      className="bg-white w-[340px] rounded-xl shadow-xl border border-gray-200"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <div className="flex items-center justify-center gap-2 px-5 py-4">
        <input
          readOnly
          value={compareStartDate ? formatCompareDate(compareStartDate) : ""}
          placeholder={lang === "AR" ? "من" : "From"}
          className="w-full border border-gray-300 px-2 py-1.5 text-[13px] rounded-md bg-gray-100 text-center"
        />

        <span className="text-gray-400 text-sm font-medium">VS</span>

        <input
          readOnly
          value={compareEndDate ? formatCompareDate(compareEndDate) : ""}
          placeholder={lang === "AR" ? "إلى" : "To"}
          className="w-full border border-gray-300 px-2 py-1.5 text-[13px] rounded-md bg-gray-100 text-center"
        />
      </div>

      <div className="px-4 pb-4">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setCalendarOffset((p) => p - 1)}
            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-md bg-white hover:bg-gray-100 shadow-sm"
          >
            ←
          </button>

          <div className="text-sm font-medium">
            {getVisibleCalendarMonthLabel()}
          </div>

          <button
            type="button"
            onClick={() => setCalendarOffset((p) => p + 1)}
            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-md bg-white hover:bg-gray-100 shadow-sm"
          >
            →
          </button>
        </div>

        <Calendar
          monthOffset={calendarOffset}
          selectedDate={selectedDate}
          calendarBaseDate={calendarBaseDate}
          setSelectedDate={setSelectedDate}
          rangeStart={compareStartDate}
          rangeEnd={compareEndDate}
          setRangeStart={setCompareStartDate}
          setRangeEnd={setCompareEndDate}
          setCalendarOffset={setCalendarOffset}
          activeQuickRange={activeQuickRange}
          setActiveQuickRange={setActiveQuickRange}
          isCompareCalendar={true}
        />
      </div>

      <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
        <button
          type="button"
          onClick={() => setShowCustomCompareModal(false)}
          className="px-3 py-1.5 text-[13px] border border-gray-300 rounded-md hover:bg-gray-100"
        >
          {TEXT.cancelBtn}
        </button>

        <button
          type="button"
          onClick={() => {
            if (compareStartDate && compareEndDate) {
              const nextCompare = {
                start: compareStartDate,
                end: compareEndDate,
              };

              const start = mainStart || new Date();
              const end = mainEnd || start;

              setCompareRange(nextCompare);
              setCompareType("custom");
              loadCards(start, end, nextCompare);
            }

            setShowCustomCompareModal(false);
          }}
          className="px-4 py-1.5 text-[13px] bg-black text-white rounded-md hover:bg-gray-800"
        >
          {TEXT.applyBtn}
        </button>
      </div>
    </div>
  </div>
)}

      {showCurrencyModal && (
  <div
    onMouseDown={() => setShowCurrencyModal(false)}
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4"
  >
    <div
      onMouseDown={(e) => e.stopPropagation()}
      className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-5"
    >
            <h3 className="text-[16px] font-semibold text-gray-900 mb-4">
              {TEXT.currencyRateTitle}
            </h3>

            <label className="block text-[12px] font-medium text-gray-700 mb-2">
              {TEXT.usdToLbp}
            </label>

            <input
              value={tempRate}
              onChange={(e) => setTempRate(e.target.value)}
              placeholder="89000"
              className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-black/10"
            />

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowCurrencyModal(false)}
                className="h-9 px-3 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
              >
                {TEXT.cancel}
              </button>

              <button
                type="button"
                onClick={() => {
                  const rate = Number(tempRate || exchangeRate);

                  if (rate > 0) {
                    setExchangeRate(rate);
                    setCurrency("LBP");
                    setShowCurrencyModal(false);
                    setTempRate("");
                  }
                }}
                className="h-9 px-3 rounded-lg bg-black text-white text-sm hover:bg-gray-900"
              >
                {TEXT.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReports;